import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdMedicalServices, MdMonitorHeart, MdMedication, MdScience,
  MdReceiptLong, MdTimeline, MdLogout, MdPrint, MdAssignment,
  MdRestaurant, MdSportsHandball, MdLocalHospital,
} from 'react-icons/md';
import { Badge } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { printDischargeSummary } from '../../utils/hospitalPdf';
import { escapeHtml } from '../../utils/escapeHtml';
import { canDischargePatient } from '../../permissions';
import {
  fluidEntriesFromActivity, emarOrdersFromActivity, doctorOrdersFromActivity, handoversFromActivity,
} from '../../utils/ipdActivityHelpers';
import { opVisitsForPatient } from '../../utils/opIpLink';
import { queueRowId } from '../../config/queueStatus';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const KIND = {
  doctor: { label: 'Doctor', tone: 'info' },
  nursing: { label: 'Nursing', tone: 'success' },
  vitals: { label: 'Vitals', tone: 'warning' },
  procedure: { label: 'Procedure', tone: 'default' },
  note: { label: 'Note', tone: 'default' },
};

const TABS = [
  { id: 'timeline', label: 'Timeline', icon: MdTimeline },
  { id: 'doctor', label: 'Ward Round', icon: MdMedicalServices },
  { id: 'orders', label: 'Doctor Orders', icon: MdAssignment },
  { id: 'nursing', label: 'Nursing & Vitals', icon: MdMonitorHeart },
  { id: 'emar', label: 'eMAR', icon: MdMedication },
  { id: 'fluid', label: 'Fluid Balance', icon: MdMonitorHeart },
  { id: 'nursingassess', label: 'Nursing Assessment', icon: MdAssignment },
  { id: 'handover', label: 'Shift Handover', icon: MdTimeline },
  { id: 'meds', label: 'Prescriptions', icon: MdMedication },
  { id: 'lab', label: 'Lab / Radiology', icon: MdScience },
  { id: 'billing', label: 'Billing', icon: MdReceiptLong },
  { id: 'tpa', label: 'TPA / Insurance', icon: MdReceiptLong },
  { id: 'carePlan', label: 'Care Plan', icon: MdMedicalServices },
  { id: 'nutrition', label: 'Nutrition', icon: MdRestaurant },
  { id: 'incident', label: 'Incident Log', icon: MdAssignment },
  { id: 'diet', label: 'Diet Orders', icon: MdRestaurant },
  { id: 'physio', label: 'Physiotherapy', icon: MdSportsHandball },
  { id: 'operation', label: 'Operation Notes', icon: MdLocalHospital },
  { id: 'procedures', label: 'Procedures', icon: MdMedicalServices },
  { id: 'transfer', label: 'Transfer', icon: MdArrowBack },
  { id: 'consent', label: 'Consent Forms', icon: MdAssignment },
];

export default function IpdChart() {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getAdmission(admissionId), [admissionId]);
  const { data: queueData } = useLiveData(() => ipApi.getQueue({ limit: 200 }), []);

  const adm = data?.admission;
  const opVisits = useMemo(
    () => opVisitsForPatient(queueData?.queue || [], adm?.patientId, { sourceQueueId: adm?.sourceQueueId }),
    [queueData, adm?.patientId, adm?.sourceQueueId],
  );
  const { data: opEmrData } = useLiveData(
    () => (adm?.patientId ? ipApi.getEmrRecords(adm.patientId, 8) : Promise.resolve({ records: [] })),
    [adm?.patientId],
  );
  const opEmrVisits = useMemo(
    () => [...(opEmrData?.records || opEmrData?.emr || opEmrData?.items || [])].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [opEmrData],
  );
  const labs = useMemo(() => data?.labs || [], [data]);
  const meds = useMemo(() => data?.meds || [], [data]);
  const activity = useMemo(() => [...(adm?.activity || [])].sort((a, b) => (b.at || '').localeCompare(a.at || '')), [adm]);

  const [tab, setTab] = useState('timeline');
  const [msg, setMsg] = useState('');

  const addActivity = useCallback(async (body) => {
    await ipApi.addAdmissionActivity(admissionId, body);
    await reload();
  }, [admissionId, reload]);

  if (loading && !adm) return <div className="page"><div className="page-body"><p className="hint">Loading chart…</p></div></div>;
  if (error && !adm) return <div className="page"><div className="page-body"><div className="error">{error}</div></div></div>;
  if (!adm) return <div className="page"><div className="page-body"><p className="hint">Admission not found.</p></div></div>;

  const discharged = adm.status === 'discharged';

  const doDischarge = async () => {
    if (!window.confirm('Discharge this patient? The final itemised bill PDF will open.')) return;
    try {
      const res = await ipApi.discharge(admissionId);
      printDischargeSummary(res?.admission || adm, res?.hospital || hospital, user);
      await reload();
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div className="page">
      <div className="page-body">
        <div className="row-actions mb" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}><MdArrowBack /> Back</button>
          <div className="row-actions" style={{ gap: 8 }}>
            {adm && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => printDischargeSummary(adm, hospital, user)}
                title="Print discharge summary / interim summary"
              >
                <MdPrint /> Discharge Summary
              </button>
            )}
            {!discharged && canDischargePatient(user?.role) && (
              <button type="button" className="btn btn-primary btn-sm" onClick={doDischarge}>
                <MdLogout /> Discharge Patient
              </button>
            )}
          </div>
        </div>

        {msg && <div className="error mb">{msg}</div>}

        <div className="ip-chart-head card mb">
          <div className="ip-chart-head-main">
            <div className="ip-avatar">{(adm.patientName || '?').slice(0, 1).toUpperCase()}</div>
            <div>
              <h2 className="ip-chart-name">{adm.patientName} <Badge tone={discharged ? 'default' : 'success'}>{discharged ? 'Discharged' : 'Under care'}</Badge></h2>
              <div className="hint">{adm.admissionId} · {adm.age ? `${adm.age}y / ${adm.gender || '—'}` : ''} · {adm.ward || '—'} · Bed {adm.bedLabel || adm.bedId || '—'}</div>
              {adm.diagnosis && <div style={{ fontSize: 12, color: '#0d6e6a', fontWeight: 600, marginTop: 2 }}>Dx: {adm.diagnosis}</div>}
              {adm.patientId && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>UHID: {adm.patientId}</div>}
              {(adm.sourceQueueId || adm.referredFrom === 'OPD Queue') && (
                <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, marginTop: 4 }}>
                  ↗ Admitted from OPD{adm.referredBy ? ` · ${adm.referredBy}` : ''}{adm.sourceQueueId ? ` · ref ${adm.sourceQueueId}` : ''}
                </div>
              )}
              {/* Critical Flags */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {adm.allergies && <span style={{ fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, padding: '2px 8px' }}>⚠ ALLERGY: {adm.allergies}</span>}
                {adm.isDnr && <span style={{ fontSize: 11, fontWeight: 700, background: '#1e1b4b', color: '#fff', borderRadius: 6, padding: '2px 8px' }}>DNR</span>}
                {adm.isIsolation && <span style={{ fontSize: 11, fontWeight: 700, background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', borderRadius: 6, padding: '2px 8px' }}>🚫 ISOLATION</span>}
                {adm.isFallRisk && <span style={{ fontSize: 11, fontWeight: 700, background: '#fefce8', color: '#ca8a04', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px' }}>⚠ FALL RISK</span>}
                {adm.bloodGroup && <span style={{ fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 8px' }}>🩸 {adm.bloodGroup}</span>}
              </div>
            </div>
          </div>
          <div className="ip-chart-meta">
            <div><span>Doctor</span><strong>{adm.doctor || '—'}</strong></div>
            <div><span>Admitted</span><strong>{dt(adm.admittedAt)}</strong></div>
            <div><span>Payer</span><strong>{adm.insurance || 'Cash'}</strong></div>
            <div><span>Running bill</span><strong className="ip-bill-amt">{inr(adm.pendingBill)}</strong></div>
          </div>
        </div>

        {(opVisits.length > 0 || opEmrVisits.length > 0) && (
          <div className="card mb" style={{ borderLeft: '4px solid #2563eb', padding: '12px 16px' }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#1d4ed8', marginBottom: 8 }}>OPD history (linked by UHID)</div>
            {opVisits.length > 0 && (
              <div style={{ marginBottom: opEmrVisits.length ? 10 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Recent OP queue / handoff</div>
                {opVisits.slice(0, 5).map((q) => (
                  <div key={queueRowId(q)} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <strong>{q.doctor}</strong> · {q.status} · {q.reason || 'Consultation'}
                    {adm?.sourceQueueId && (q.queueId === adm.sourceQueueId || q.id === adm.sourceQueueId) && (
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#2563eb' }}>SOURCE VISIT</span>
                    )}
                    {q.preVitals?.bp && <span className="hint"> · BP {q.preVitals.bp}</span>}
                  </div>
                ))}
              </div>
            )}
            {opEmrVisits.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Recent OP EMR / Rx</div>
                {opEmrVisits.slice(0, 5).map((v) => {
                  const rxText = v.prescription
                    || (Array.isArray(v.rxLines) ? v.rxLines.filter((r) => r.drug).map((r) => {
                      const dosing = r.frequency || ([r.m, r.a, r.n].some((x) => x != null && x !== '') ? `${r.m || 0}-${r.a || 0}-${r.n || 0}` : '');
                      return `${r.drug} ${r.dose || ''} ${dosing} ${r.duration || ''}`.trim();
                    }).join('; ') : '');
                  return (
                  <div key={v.recordId || v.createdAt} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                    {dt(v.createdAt)} · {v.doctorName || v.doctor || 'OPD'}
                    {v.diagnosis && <> · <em>{v.diagnosis}</em></>}
                    {v.complaint && !v.diagnosis && <> · <em>{v.complaint}</em></>}
                    {rxText && <div className="hint" style={{ marginTop: 2 }}>Rx: {String(rxText).slice(0, 160)}{String(rxText).length > 160 ? '…' : ''}</div>}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {!opVisits.length && !opEmrVisits.length && adm?.patientId && (
          <div className="card mb hint" style={{ borderLeft: '4px solid #94a3b8' }}>
            No linked OPD visits found for UHID {adm.patientId} in the last 7 days. Save an EMR/Rx on OP before admit to see clinical handoff here.
          </div>
        )}
        {!adm?.patientId && (
          <div className="card mb" style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
            This admission has no UHID — OP history cannot link. Re-admit from OP with a registered patient, or update the patient ID on the chart demographics.
          </div>
        )}

        <div className="ip-tabs card mb">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={`emr-chip ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <t.icon style={{ marginRight: 6, verticalAlign: '-2px' }} />{t.label}
            </button>
          ))}
        </div>

        {tab === 'timeline' && <Timeline activity={activity} />}
        {tab === 'doctor' && <NotesTab kind="doctor" activity={activity} discharged={discharged} onAdd={addActivity} />}
        {tab === 'nursing' && <NursingTab activity={activity} discharged={discharged} onAdd={addActivity} />}
        {tab === 'meds' && <MedsTab meds={meds} />}
        {tab === 'lab' && <LabTab labs={labs} adm={adm} />}
        {tab === 'billing' && <BillingTab adm={adm} discharged={discharged} onReload={reload} onError={setMsg} onAdd={addActivity} />}
        {tab === 'diet' && <DietTab adm={adm} discharged={discharged} onError={setMsg} />}
        {tab === 'physio' && <PhysioTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} />}
        {tab === 'operation' && <OperationNotesTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} />}
        {tab === 'procedures' && <ProcedureLogTab adm={adm} discharged={discharged} onAdd={addActivity} />}
        {tab === 'transfer' && <TransferTab adm={adm} discharged={discharged} onReload={reload} onError={setMsg} />}
        {tab === 'consent' && <ConsentTab adm={adm} onAdd={addActivity} discharged={discharged} />}
        {tab === 'emar' && <EmarTab adm={adm} activity={activity} discharged={discharged} onAdd={addActivity} user={user} />}
        {tab === 'fluid' && <FluidBalanceTab adm={adm} activity={activity} discharged={discharged} onAdd={addActivity} user={user} />}
        {tab === 'nursingassess' && <NursingAssessmentTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} />}
        {tab === 'tpa' && <TpaInsuranceTab adm={adm} discharged={discharged} onReload={reload} onError={setMsg} />}
        {tab === 'orders' && <DoctorOrdersTab adm={adm} activity={activity} discharged={discharged} onAdd={addActivity} user={user} />}
        {tab === 'handover' && <ShiftHandoverTab adm={adm} activity={activity} discharged={discharged} onAdd={addActivity} user={user} />}
        {tab === 'carePlan' && <CarePlanTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} admissionId={admissionId} onReload={reload} />}
        {tab === 'nutrition' && <NutritionalScreeningTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} />}
        {tab === 'incident' && <IncidentLogTab adm={adm} discharged={discharged} activity={activity} onAdd={addActivity} />}
      </div>
    </div>
  );
}

/* ── Diet Tab for IPD ── */
const DIET_TYPES_IPD = ['Normal', 'Diabetic', 'Low Sodium', 'Low Fat', 'High Protein', 'Liquid', 'Soft Diet', 'Renal Diet', 'Post-Op', 'NPO (Nothing by Mouth)'];
function DietTab({ adm, discharged, onError }) {
  const [dietType, setDietType] = useState('Normal');
  const [allergy, setAllergy] = useState('');
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ipApi.getDietOrders(200);
        const order = (res?.orders || []).find((o) => o.admissionId === adm.admissionId);
        if (cancelled || !order) return;
        setDietType(order.dietType || 'Normal');
        setAllergy(order.allergyNote || '');
        setInstructions(order.spInstruction || '');
        setLoaded(true);
      } catch (_) { /* ignore — form stays default */ }
    })();
    return () => { cancelled = true; };
  }, [adm.admissionId]);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await ipApi.saveDietOrder(adm.admissionId, {
        dietType,
        allergyNote: allergy,
        spInstruction: instructions,
        patientName: adm.patientName,
        ward: adm.ward,
        bed: adm.bedLabel || adm.bedId,
        bedLabel: adm.bedLabel || adm.bedId,
      });
      setSaved(true);
      setLoaded(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>Diet Orders — {adm.patientName}</h3>
      {loaded && <p className="hint" style={{ marginTop: 0 }}>Showing saved kitchen order for this admission.</p>}
      {dietType === 'NPO (Nothing by Mouth)' && (
        <div className="diet-npo-banner">⚠ NPO — DO NOT SERVE FOOD OR LIQUIDS — Confirm with doctor before serving anything</div>
      )}
      <div className="form-grid" style={{ marginTop: 14 }}>
        <div className="field">
          <label>Diet type *</label>
          <select className="input" value={dietType} onChange={(e) => setDietType(e.target.value)} disabled={discharged}>
            {DIET_TYPES_IPD.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Allergy / Intolerance</label>
          <input className="input" placeholder="e.g. No eggs, No dairy" value={allergy} onChange={(e) => setAllergy(e.target.value)} disabled={discharged} />
        </div>
        <div className="field full">
          <label>Special Instructions</label>
          <textarea className="input" rows={2} placeholder="e.g. Low spice, extra soft, small frequent meals" value={instructions} onChange={(e) => setInstructions(e.target.value)} disabled={discharged} />
        </div>
      </div>
      {!discharged && (
        <div className="form-actions" style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save Diet Order'}</button>
          {saved && <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
        </div>
      )}
      <div className="info-banner" style={{ marginTop: 14 }}>
        Diet order syncs to the <strong>Diet Management</strong> kitchen module when saved.
      </div>
    </div>
  );
}

/* ── Physiotherapy Tab ── */
function PhysioTab({ adm, discharged, activity, onAdd }) {
  const sessions = (activity || []).filter((e) => e.kind === 'physio').map((e) => ({ ...(e.data || e), _entryId: e.entryId }));
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), therapist: '', type: 'Active ROM Exercises', duration: 30, notes: '', progress: 'Stable' });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const PHYSIO_TYPES = ['Active ROM Exercises', 'Passive ROM', 'Strengthening', 'Gait Training', 'Breathing Exercises', 'Heat Therapy', 'TENS', 'Ultrasound Therapy', 'Massage', 'Other'];

  const addSession = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await onAdd({ kind: 'physio', title: `${form.type} — ${form.therapist || 'Physiotherapist'}`, note: form.notes, data: { ...form } });
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0,10), therapist: '', type: 'Active ROM Exercises', duration: 30, notes: '', progress: 'Stable' });
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Physiotherapy Sessions — {adm.patientName}</h3>
        {!discharged && <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add Session</button>}
      </div>
      {err && <div className="error mb">{err}</div>}
      {showForm && (
        <form className="card mb form-grid" onSubmit={addSession}>
          <h4 style={{ gridColumn: '1/-1', margin: 0 }}>New Physio Session</h4>
          <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="field"><label>Therapist</label><input className="input" value={form.therapist} onChange={(e) => setForm({ ...form, therapist: e.target.value })} /></div>
          <div className="field"><label>Type</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {PHYSIO_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Duration (mins)</label><input type="number" className="input" value={form.duration} onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })} /></div>
          <div className="field"><label>Progress</label>
            <select className="input" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })}>
              {['Improving', 'Stable', 'Declined', 'Goal Met'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="field full"><label>Session Notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save Session'}</button>
          </div>
        </form>
      )}
      {sessions.length === 0 && !showForm && <p className="hint table-empty">No physiotherapy sessions recorded.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sessions.map((s) => (
          <div key={s._entryId || s.id} className="ip-entry card" style={{ border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>{s.type}</div><div style={{ fontSize: 12, color: '#64748b' }}>{s.date} · {s.therapist} · {s.duration} mins</div></div>
              <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: s.progress === 'Improving' ? '#f0fdf4' : s.progress === 'Declined' ? '#fef2f2' : '#f8fafc', color: s.progress === 'Improving' ? '#16a34a' : s.progress === 'Declined' ? '#dc2626' : '#475569', border: `1px solid ${s.progress === 'Improving' ? '#86efac' : s.progress === 'Declined' ? '#fca5a5' : '#e2e8f0'}` }}>{s.progress}</span>
            </div>
            {s.notes && <div style={{ fontSize: 13, color: '#334155', fontStyle: 'italic' }}>{s.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Operation Notes Tab ── */
function OperationNotesTab({ adm, discharged, activity, onAdd }) {
  const opNotes = (activity || []).filter((e) => e.kind === 'operation_note').map((e) => ({ ...(e.data || e), _entryId: e.entryId }));
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10), procedure: '', surgeon: '', anesthetist: '',
    anesthesiaType: 'General', duration: '', findings: '', complications: 'None', postOpOrders: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const addNote = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await onAdd({ kind: 'operation_note', title: `${form.procedure} — ${form.surgeon || 'Surgeon'}`, note: form.findings || form.postOpOrders, data: { ...form } });
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0,10), procedure: '', surgeon: '', anesthetist: '', anesthesiaType: 'General', duration: '', findings: '', complications: 'None', postOpOrders: '' });
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Operation Notes — {adm.patientName}</h3>
        {!discharged && <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add Op Note</button>}
      </div>
      {err && <div className="error mb">{err}</div>}
      {showForm && (
        <form className="card mb form-grid" onSubmit={addNote}>
          <h4 style={{ gridColumn: '1/-1', margin: 0 }}>Operation Note</h4>
          <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="field"><label>Procedure / Surgery *</label><input className="input" required value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} placeholder="e.g. Appendectomy, Cholecystectomy" /></div>
          <div className="field"><label>Surgeon</label><input className="input" value={form.surgeon} onChange={(e) => setForm({ ...form, surgeon: e.target.value })} /></div>
          <div className="field"><label>Anesthetist</label><input className="input" value={form.anesthetist} onChange={(e) => setForm({ ...form, anesthetist: e.target.value })} /></div>
          <div className="field"><label>Anesthesia type</label>
            <select className="input" value={form.anesthesiaType} onChange={(e) => setForm({ ...form, anesthesiaType: e.target.value })}>
              {['General', 'Spinal', 'Epidural', 'Local', 'Sedation'].map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="field"><label>Duration (mins)</label><input type="number" className="input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
          <div className="field full"><label>Intraoperative findings</label><textarea className="input" rows={2} value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} placeholder="Describe findings..." /></div>
          <div className="field"><label>Complications</label><input className="input" value={form.complications} onChange={(e) => setForm({ ...form, complications: e.target.value })} /></div>
          <div className="field full"><label>Post-op orders</label><textarea className="input" rows={2} value={form.postOpOrders} onChange={(e) => setForm({ ...form, postOpOrders: e.target.value })} placeholder="e.g. NPO x6h, IV fluids, antibiotics..." /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save Operation Note'}</button>
          </div>
        </form>
      )}
      {opNotes.length === 0 && !showForm && <p className="hint table-empty">No operation notes. Click "+ Add Op Note" to record surgical details.</p>}
      {opNotes.map((op) => (
        <div key={op._entryId || op.id} className="card mb" style={{ border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div><div style={{ fontWeight: 800, fontSize: 15 }}>{op.procedure}</div><div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{op.date} · {op.surgeon} · {op.anesthesiaType} anesthesia · {op.duration} mins</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
            <div><strong>Anesthetist:</strong> {op.anesthetist || '—'}</div>
            <div><strong>Complications:</strong> {op.complications || 'None'}</div>
            <div style={{ gridColumn: '1/-1' }}><strong>Findings:</strong> {op.findings || '—'}</div>
            <div style={{ gridColumn: '1/-1' }}><strong>Post-op orders:</strong> {op.postOpOrders || '—'}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EntryCard({ e }) {
  const k = KIND[e.kind] || KIND.note;
  return (
    <div className="ip-entry">
      <div className="ip-entry-side"><Badge tone={k.tone}>{k.label}</Badge></div>
      <div className="ip-entry-body">
        <div className="ip-entry-meta">{dt(e.at)} · {e.byName || e.by}{e.role ? ` (${e.role})` : ''}</div>
        {e.title && <div className="ip-entry-title">{e.title}</div>}
        {e.kind === 'vitals' && e.vitals ? (
          <div className="ip-vitals-row">
            {Object.entries({ BP: e.vitals.bp, Pulse: e.vitals.pulse, 'Temp °F': e.vitals.temp, 'SpO₂ %': e.vitals.spo2, 'RR': e.vitals.rr, 'Wt kg': e.vitals.weight })
              .filter(([, v]) => v)
              .map(([label, v]) => <span key={label} className="ip-vital-chip"><em>{label}</em> {v}</span>)}
          </div>
        ) : null}
        {e.note && <div className="ip-entry-note">{e.note}</div>}
      </div>
    </div>
  );
}

function Timeline({ activity }) {
  if (!activity.length) return <p className="hint table-empty">No entries yet. Notes, vitals and procedures logged for this patient will appear here with date &amp; time.</p>;
  return <div className="card">{activity.map((e) => <EntryCard key={e.entryId} e={e} />)}</div>;
}

function NotesTab({ kind, activity, discharged, onAdd }) {
  const [mode, setMode] = useState('soap'); // 'soap' | 'quick'
  // SOAP fields
  const [soap, setSoap] = useState({ subjective: '', objective: '', assessment: '', plan: '', roundType: 'Morning Round', roundBy: '', roundAt: new Date().toISOString().slice(0, 16) });
  // Quick note fields
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const entries = activity.filter((e) => e.kind === kind);

  const saveSoap = async () => {
    if (!soap.assessment.trim() && !soap.plan.trim()) return;
    setBusy(true);
    const composed = [
      soap.subjective ? `**S (Subjective):** ${soap.subjective}` : '',
      soap.objective ? `**O (Objective):** ${soap.objective}` : '',
      soap.assessment ? `**A (Assessment):** ${soap.assessment}` : '',
      soap.plan ? `**P (Plan):** ${soap.plan}` : '',
    ].filter(Boolean).join('\n\n');
    try {
      await onAdd({ kind, title: `${soap.roundType} — ${soap.roundBy || 'Doctor'}`, note: composed });
      setSoap({ subjective: '', objective: '', assessment: '', plan: '', roundType: 'Morning Round', roundBy: '', roundAt: new Date().toISOString().slice(0, 16) });
    } finally { setBusy(false); }
  };

  const saveQuick = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await onAdd({ kind, title, note }); setNote(''); setTitle(''); } finally { setBusy(false); }
  };

  return (
    <>
      {!discharged && (
        <div className="card mb">
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', width: 'fit-content' }}>
            {[['soap', '📋 SOAP Ward Round'], ['quick', '⚡ Quick Note']].map(([m, l]) => (
              <button key={m} type="button" onClick={() => setMode(m)} style={{ padding: '6px 18px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: mode === m ? '#0d6e6a' : '#f8fafc', color: mode === m ? '#fff' : '#475569', transition: 'all 0.15s' }}>{l}</button>
            ))}
          </div>

          {mode === 'soap' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Round Type</label>
                  <select className="input" value={soap.roundType} onChange={e => setSoap(s => ({ ...s, roundType: e.target.value }))}>
                    {['Morning Round', 'Evening Round', 'Night Round', 'Emergency Review', 'Consultant Review', 'Discharge Review'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Doctor Name</label>
                  <input className="input" value={soap.roundBy} onChange={e => setSoap(s => ({ ...s, roundBy: e.target.value }))} placeholder="Dr. Name" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Date & Time</label>
                  <input type="datetime-local" className="input" value={soap.roundAt} onChange={e => setSoap(s => ({ ...s, roundAt: e.target.value }))} />
                </div>
              </div>
              {[['subjective', '🗣 S — Subjective', 'Patient complaints, symptoms, history updates, patient/family concerns…'], ['objective', '🩺 O — Objective', 'Vitals at round, physical exam findings, relevant lab/radiology values…'], ['assessment', '🧠 A — Assessment', 'Working diagnosis, current clinical impression, problem list…'], ['plan', '📋 P — Plan', 'Investigation orders, medication changes, diet, activity, review plan, follow-up…']].map(([key, label, placeholder]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ fontWeight: 800, color: '#0d6e6a', fontSize: 13, display: 'block', marginBottom: 4 }}>{label}</label>
                  <textarea className="input" rows={key === 'plan' ? 3 : 2} value={soap[key]} onChange={e => setSoap(s => ({ ...s, [key]: e.target.value }))} placeholder={placeholder} />
                </div>
              ))}
              <div className="form-actions">
                <button type="button" className="btn btn-primary" disabled={busy || (!soap.assessment.trim() && !soap.plan.trim())} onClick={saveSoap}>{busy ? 'Saving…' : '✓ Save SOAP Ward Round Note'}</button>
              </div>
            </>
          )}

          {mode === 'quick' && (
            <>
              <div className="field mb"><label>Title (optional)</label><input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Ward round / Progress note / Procedure" /></div>
              <div className="field mb"><label>Note</label><textarea className="input" rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="Clinical findings, plan, orders…" /></div>
              <div className="form-actions"><button type="button" className="btn btn-primary" disabled={busy || !note.trim()} onClick={saveQuick}>{busy ? 'Saving…' : 'Add Note'}</button></div>
            </>
          )}
        </div>
      )}
      {entries.length ? <div className="card">{entries.map((e) => <EntryCard key={e.entryId} e={e} />)}</div> : <p className="hint table-empty">No doctor notes yet.</p>}
    </>
  );
}

function NursingTab({ activity, discharged, onAdd }) {
  const [v, setV] = useState({ bp: '', pulse: '', temp: '', spo2: '', rr: '', weight: '', gcs: '', pain: '' });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const vitals = activity.filter((e) => e.kind === 'vitals');
  const notes = activity.filter((e) => e.kind === 'nursing');

  const saveVitals = async () => {
    if (!Object.values(v).some(Boolean)) return;
    setBusy('vitals');
    try { await onAdd({ kind: 'vitals', vitals: v, note: '' }); setV({ bp: '', pulse: '', temp: '', spo2: '', rr: '', weight: '', gcs: '', pain: '' }); } finally { setBusy(''); }
  };
  const saveNote = async () => {
    if (!note.trim()) return;
    setBusy('note');
    try { await onAdd({ kind: 'nursing', note }); setNote(''); } finally { setBusy(''); }
  };

  // Vital alert check
  const vitalAlert = (key, val) => {
    if (!val) return null;
    const n = parseFloat(val);
    const rules = { pulse: n < 50 || n > 120, spo2: n < 94, rr: n < 10 || n > 30, temp: n > 100.4 || n < 96, pain: n >= 7 };
    return rules[key] ? { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' } : null;
  };

  // Trend data for last 10 vitals
  const last10 = vitals.slice(0, 10).reverse();

  return (
    <>
      {!discharged && (
        <div className="card mb">
          <h3 style={{ marginTop: 0 }}>Record Vitals</h3>
          <div className="form-grid mb">
            {[['bp','BP (mmHg)','120/80'],['pulse','Pulse (bpm)','72'],['temp','Temp °F','98.4'],['spo2','SpO₂ %','98'],['rr','Resp. Rate','18'],['weight','Weight (kg)','64'],['gcs','GCS (/15)','15'],['pain','Pain (0-10)','0']].map(([key, label, ph]) => {
              const alert = vitalAlert(key, v[key]);
              return (
                <div key={key} className="field">
                  <label>{label}</label>
                  <input className="input" placeholder={ph} value={v[key]} onChange={e => setV({ ...v, [key]: e.target.value })} style={alert ? { borderColor: alert.color, background: alert.bg } : {}} />
                  {alert && <div style={{ fontSize: 10.5, color: alert.color, fontWeight: 700, marginTop: 2 }}>⚠ Out of range</div>}
                </div>
              );
            })}
          </div>
          <div className="form-actions">
            <input className="input" style={{ flex: 1 }} placeholder="Nursing note (saved separately)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button type="button" className="btn btn-secondary" disabled={busy === 'note' || !note.trim()} onClick={saveNote}>Add note</button>
            <button type="button" className="btn btn-primary" disabled={busy === 'vitals'} onClick={saveVitals}>Save vitals</button>
          </div>
        </div>
      )}

      {/* Vitals Trend Summary */}
      {last10.length > 0 && (
        <div className="card mb">
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>📊 Vitals Trend — Last {last10.length} Reading{last10.length > 1 ? 's' : ''}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>BP</th>
                  <th>Pulse</th>
                  <th>Temp °F</th>
                  <th>SpO₂ %</th>
                  <th>RR</th>
                  <th>GCS</th>
                  <th>Pain</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {last10.map((e) => {
                  const vv = e.vitals || {};
                  const pulseAlert = vv.pulse && (parseFloat(vv.pulse) < 50 || parseFloat(vv.pulse) > 120);
                  const spo2Alert = vv.spo2 && parseFloat(vv.spo2) < 94;
                  const tempAlert = vv.temp && (parseFloat(vv.temp) > 100.4 || parseFloat(vv.temp) < 96);
                  const painAlert = vv.pain && parseFloat(vv.pain) >= 7;
                  return (
                    <tr key={e.entryId}>
                      <td style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{dt(e.at)}</td>
                      <td>{vv.bp || '—'}</td>
                      <td style={{ color: pulseAlert ? '#dc2626' : undefined, fontWeight: pulseAlert ? 700 : 400 }}>{vv.pulse || '—'}{pulseAlert ? ' ⚠' : ''}</td>
                      <td style={{ color: tempAlert ? '#dc2626' : undefined, fontWeight: tempAlert ? 700 : 400 }}>{vv.temp || '—'}{tempAlert ? ' ⚠' : ''}</td>
                      <td style={{ color: spo2Alert ? '#dc2626' : undefined, fontWeight: spo2Alert ? 700 : 400 }}>{vv.spo2 || '—'}{spo2Alert ? ' ⚠' : ''}</td>
                      <td>{vv.rr || '—'}</td>
                      <td>{vv.gcs || '—'}</td>
                      <td style={{ color: painAlert ? '#dc2626' : undefined, fontWeight: painAlert ? 700 : 400 }}>{vv.pain || '—'}{painAlert ? ' 🔴' : ''}</td>
                      <td className="hint">{e.byName || e.by}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mini visual trend row */}
          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            {[['SpO₂', last10.map(e => e.vitals?.spo2), '%', v => parseFloat(v) < 94], ['Pulse', last10.map(e => e.vitals?.pulse), 'bpm', v => parseFloat(v) < 50 || parseFloat(v) > 120], ['Pain', last10.map(e => e.vitals?.pain), '/10', v => parseFloat(v) >= 7]].map(([label, vals, unit, isAlert]) => (
              <div key={label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', minWidth: 120 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 28 }}>
                  {vals.filter(Boolean).map((v, i) => {
                    const n = parseFloat(v);
                    const alert = isAlert(v);
                    const height = Math.min(28, Math.max(4, (n / 100) * 28));
                    return <div key={i} style={{ width: 6, borderRadius: 3, background: alert ? '#dc2626' : '#0d6e6a', height: height + 'px', alignSelf: 'flex-end', title: `${v} ${unit}` }} title={`${v} ${unit}`} />;
                  })}
                  {vals.filter(Boolean).length === 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>No data</span>}
                </div>
                <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>Latest: {vals.filter(Boolean).pop() || '—'} {unit}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {notes.length > 0 && <div className="card">{notes.map((e) => <EntryCard key={e.entryId} e={e} />)}</div>}
      {!vitals.length && !notes.length && <p className="hint table-empty">No vitals or nursing notes charted yet.</p>}
    </>
  );
}

function MedsTab({ meds }) {
  if (!meds.length) return <p className="hint table-empty">No pharmacy orders linked to this patient yet.</p>;
  return (
    <div className="card"><div className="table-wrap">
      <table className="data-table">
        <thead><tr><th>Date</th><th>Medicines</th><th>Amount</th><th>Status</th></tr></thead>
        <tbody>
          {meds.map((m) => (
            <tr key={m.orderId}><td>{dt(m.createdAt)}</td><td>{m.items || '—'}</td><td>{inr(m.amount)}</td><td><Badge tone={m.status === 'dispensed' ? 'success' : 'warning'}>{m.status}</Badge></td></tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}

const RADIOLOGY_MODALITIES = ['X-Ray', 'CT Scan', 'MRI', 'Ultrasound (USG)', 'Echo (2D)', 'Nuclear Medicine', 'Fluoroscopy', 'Mammography', 'PET Scan', 'DEXA Scan'];
const BLOOD_COMPONENTS = ['Packed Red Blood Cells (PRBC)', 'Whole Blood', 'Platelets (SDP)', 'Fresh Frozen Plasma (FFP)', 'Cryoprecipitate', 'Albumin 5%', 'Albumin 25%', 'IVIG'];

function LabTab({ labs, adm }) {
  const [labTab, setLabTab] = useState('results');
  const { data: radData, reload: reloadRad } = useLiveData(
    () => ipApi.getRadiologyOrders({ patientId: adm?.patientId, limit: 50 }),
    [adm?.patientId],
  );
  const { records: allBloodReqs, create: createBloodReq, loading: bloodLoading } = useHmsRecords('bloodbank-request', 200);
  const radioOrders = useMemo(
    () => (radData?.orders || []).filter((o) => o.patientId === adm?.patientId || o.admissionId === adm?.admissionId),
    [radData, adm],
  );
  const bloodOrders = useMemo(() => (
    (allBloodReqs || [])
      .filter((r) => r.patientId === adm?.patientId || r.admissionId === adm?.admissionId || r.uhid === adm?.patientId)
      .map((r) => ({
        id: r.recordId || r.id,
        component: r.component || 'Blood component',
        units: r.units,
        bloodGroup: r.group || r.bloodGroup,
        urgency: r.urgency || 'Routine',
        orderedBy: r.reqBy || r.orderedBy,
        requestedAt: r.date || r.createdAt,
        status: r.status || 'Pending',
        crossmatch: r.crossmatch,
        indication: r.reason || r.indication,
      }))
  ), [allBloodReqs, adm]);
  const [radioForm, setRadioForm] = useState({ modality: 'X-Ray', bodyPart: '', indication: '', urgency: 'Routine', contrast: false, orderedBy: '' });
  const [bloodForm, setBloodForm] = useState({ component: 'Packed Red Blood Cells (PRBC)', units: 1, indication: '', urgency: 'Routine', crossmatch: true, bloodGroup: adm?.bloodGroup || '', orderedBy: '' });
  const [showRadioForm, setShowRadioForm] = useState(false);
  const [showBloodForm, setShowBloodForm] = useState(false);
  const [radioBusy, setRadioBusy] = useState(false);
  const [bloodBusy, setBloodBusy] = useState(false);

  async function addRadiology(e) {
    e.preventDefault();
    if (!radioForm.bodyPart.trim()) return;
    setRadioBusy(true);
    try {
      await ipApi.createRadiologyOrder({
        patientName: adm.patientName,
        patientId: adm.patientId,
        admissionId: adm.admissionId,
        modality: radioForm.modality,
        study: radioForm.bodyPart,
        bodyPart: radioForm.bodyPart,
        clinicalInfo: radioForm.indication,
        priority: radioForm.urgency,
        contrast: radioForm.contrast ? 'With Contrast (IV)' : 'Without Contrast',
        referredBy: radioForm.orderedBy || adm.doctor,
        amount: ({ 'X-Ray': 500, USG: 800, CT: 3500, MRI: 6000, Echo: 1500, ECG: 300, Mammography: 1200 }[radioForm.modality] || 500),
        source: 'ipd_chart',
      });
      setRadioForm({ modality: 'X-Ray', bodyPart: '', indication: '', urgency: 'Routine', contrast: false, orderedBy: '' });
      setShowRadioForm(false);
      await reloadRad();
    } finally {
      setRadioBusy(false);
    }
  }

  async function addBloodOrder(e) {
    e.preventDefault();
    if (!adm?.patientId) return;
    setBloodBusy(true);
    try {
      await createBloodReq({
        patient: adm.patientName,
        uhid: adm.patientId,
        patientId: adm.patientId,
        admissionId: adm.admissionId,
        component: bloodForm.component,
        group: bloodForm.bloodGroup,
        units: bloodForm.units,
        reason: bloodForm.indication,
        reqBy: bloodForm.orderedBy || adm.doctor,
        urgency: bloodForm.urgency,
        crossmatch: bloodForm.crossmatch,
        status: 'Pending',
        date: new Date().toISOString().slice(0, 10),
        source: 'ipd_chart',
      });
      setBloodForm({ component: 'Packed Red Blood Cells (PRBC)', units: 1, indication: '', urgency: 'Routine', crossmatch: true, bloodGroup: adm?.bloodGroup || '', orderedBy: '' });
      setShowBloodForm(false);
    } finally {
      setBloodBusy(false);
    }
  }

  const urgencyColor = { Routine: '#10b981', Urgent: '#f97316', 'STAT': '#dc2626', 'STAT (Immediate)': '#dc2626' };

  return (
    <div>
      {/* Sub-tab strip */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', width: 'fit-content' }}>
        {[['results','🧪 Lab Results'], ['radiology','🩻 Radiology Orders'], ['bloodbank','🩸 Blood Bank']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setLabTab(id)} style={{ padding: '7px 18px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: labTab === id ? '#0d6e6a' : '#f8fafc', color: labTab === id ? '#fff' : '#475569', transition: 'all 0.15s' }}>{label}</button>
        ))}
      </div>

      {/* Lab Results */}
      {labTab === 'results' && (
        labs.length ? (
          <div className="card"><div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Sample ID</th><th>Tests</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {labs.map((l) => (
                  <tr key={l.orderId}><td>{dt(l.createdAt)}</td><td>{l.orderId}</td><td>{l.test || (l.tests || []).map((t) => t.name).join(', ')}</td><td>{inr(l.amount)}</td><td><Badge tone={['verified', 'Approved', 'completed'].includes(l.status) ? 'success' : 'warning'}>{l.status}</Badge></td></tr>
                ))}
              </tbody>
            </table>
          </div></div>
        ) : <p className="hint table-empty">No lab orders linked to this patient yet.</p>
      )}

      {/* Radiology Orders */}
      {labTab === 'radiology' && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <button type="button" className="btn btn-primary" onClick={() => setShowRadioForm(s => !s)}>{showRadioForm ? '✕ Cancel' : '+ Order Radiology Investigation'}</button>
          </div>
          {showRadioForm && (
            <form className="card mb" style={{ borderLeft: '4px solid #6366f1' }} onSubmit={addRadiology}>
              <h3 style={{ marginTop: 0, color: '#6366f1' }}>🩻 Radiology Order</h3>
              <div className="form-grid mb">
                <div className="field"><label>Modality</label><select className="input" value={radioForm.modality} onChange={e => setRadioForm(f => ({ ...f, modality: e.target.value }))}>{RADIOLOGY_MODALITIES.map(m => <option key={m}>{m}</option>)}</select></div>
                <div className="field"><label>Body Part / Region *</label><input className="input" required value={radioForm.bodyPart} onChange={e => setRadioForm(f => ({ ...f, bodyPart: e.target.value }))} placeholder="e.g. Chest PA, Abdomen AP, Brain with contrast" /></div>
                <div className="field"><label>Urgency</label><select className="input" value={radioForm.urgency} onChange={e => setRadioForm(f => ({ ...f, urgency: e.target.value }))}><option>Routine</option><option>Urgent</option><option>STAT (Immediate)</option></select></div>
                <div className="field"><label>Ordered By</label><input className="input" value={radioForm.orderedBy} onChange={e => setRadioForm(f => ({ ...f, orderedBy: e.target.value }))} placeholder="Dr. Name" /></div>
                <div className="field"><label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}><input type="checkbox" checked={radioForm.contrast} onChange={e => setRadioForm(f => ({ ...f, contrast: e.target.checked }))} /> Contrast Required</label></div>
                <div className="field full"><label>Clinical Indication</label><input className="input" value={radioForm.indication} onChange={e => setRadioForm(f => ({ ...f, indication: e.target.value }))} placeholder="Reason for ordering (diagnosis / clinical query)" /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRadioForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={radioBusy}>{radioBusy ? 'Submitting…' : '✓ Submit Radiology Order'}</button>
              </div>
            </form>
          )}
          {radioOrders.length === 0 && !showRadioForm && <p className="hint table-empty">No radiology orders placed yet.</p>}
          {radioOrders.map((r) => (
            <div key={r.orderId || r.id} className="card mb" style={{ borderLeft: '4px solid #6366f1' }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:6 }}>
                <div><div style={{ fontWeight:800, fontSize:14 }}>{r.modality} — {r.bodyPart || r.study}</div><div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>By: {r.referredBy || r.orderedBy || '—'} · {new Date(r.createdAt || r.orderedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div></div>
                <div style={{ display:'flex', gap:6 }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:999, color:urgencyColor[r.priority || r.urgency]||'#64748b', border:`1px solid ${urgencyColor[r.priority || r.urgency]||'#e2e8f0'}`, background:'#fff' }}>{r.priority || r.urgency}</span>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:999, color:'#f59e0b', border:'1px solid #fde68a', background:'#fffbeb' }}>{r.status}</span>
                  {r.contrast && String(r.contrast).toLowerCase().includes('with') && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999, background:'#fef3c7', color:'#92400e', border:'1px solid #fcd34d' }}>+ Contrast</span>}
                </div>
              </div>
              {(r.clinicalInfo || r.indication) && <div style={{ fontSize:12.5, color:'#475569' }}><strong>Indication:</strong> {r.clinicalInfo || r.indication}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Blood Bank */}
      {labTab === 'bloodbank' && (
        <div>
          {bloodLoading && <p className="hint mb">Loading blood requests…</p>}
          <div style={{ marginBottom: 14 }}>
            <button type="button" className="btn btn-primary" style={{ background:'#dc2626', borderColor:'#dc2626' }} onClick={() => setShowBloodForm(s => !s)}>{showBloodForm ? '✕ Cancel' : '🩸 Request Blood Component'}</button>
          </div>
          {showBloodForm && (
            <form className="card mb" style={{ borderLeft: '4px solid #dc2626' }} onSubmit={addBloodOrder}>
              <h3 style={{ marginTop: 0, color: '#dc2626' }}>🩸 Blood Bank Request</h3>
              <div className="form-grid mb">
                <div className="field"><label>Blood Component *</label><select className="input" value={bloodForm.component} onChange={e => setBloodForm(f => ({ ...f, component: e.target.value }))}>{BLOOD_COMPONENTS.map(c => <option key={c}>{c}</option>)}</select></div>
                <div className="field"><label>Units Required</label><input type="number" className="input" min={1} max={20} value={bloodForm.units} onChange={e => setBloodForm(f => ({ ...f, units: Number(e.target.value) }))} /></div>
                <div className="field"><label>Patient Blood Group</label><input className="input" value={bloodForm.bloodGroup} onChange={e => setBloodForm(f => ({ ...f, bloodGroup: e.target.value }))} placeholder="A+, B-, O+, etc." /></div>
                <div className="field"><label>Urgency</label><select className="input" value={bloodForm.urgency} onChange={e => setBloodForm(f => ({ ...f, urgency: e.target.value }))}><option>Routine</option><option>Urgent</option><option>STAT (Immediate)</option><option>Massive Transfusion Protocol (MTP)</option></select></div>
                <div className="field"><label>Ordered By</label><input className="input" value={bloodForm.orderedBy} onChange={e => setBloodForm(f => ({ ...f, orderedBy: e.target.value }))} placeholder="Dr. Name" /></div>
                <div className="field"><label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}><input type="checkbox" checked={bloodForm.crossmatch} onChange={e => setBloodForm(f => ({ ...f, crossmatch: e.target.checked }))} /> Crossmatch Required</label></div>
                <div className="field full"><label>Clinical Indication</label><input className="input" value={bloodForm.indication} onChange={e => setBloodForm(f => ({ ...f, indication: e.target.value }))} placeholder="e.g. Hb 6.2 g/dL, pre-operative, GI bleed" /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBloodForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ background:'#dc2626', borderColor:'#dc2626' }} disabled={bloodBusy}>{bloodBusy ? 'Submitting…' : '🩸 Submit Request'}</button>
              </div>
            </form>
          )}
          {bloodOrders.length === 0 && !showBloodForm && <p className="hint table-empty">No blood component requests placed yet. Patient blood group: <strong>{adm?.bloodGroup || 'Not recorded'}</strong></p>}
          {bloodOrders.map(b => (
            <div key={b.id} className="card mb" style={{ borderLeft: '4px solid #dc2626' }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                <div><div style={{ fontWeight:800, fontSize:14, color:'#dc2626' }}>{b.component} × {b.units} unit{b.units > 1 ? 's' : ''}</div><div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>By: {b.orderedBy || '—'} · Group: {b.bloodGroup || '—'} · {new Date(b.requestedAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div></div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'flex-start' }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:999, color:urgencyColor[b.urgency]||'#64748b', border:`1px solid ${urgencyColor[b.urgency]||'#e2e8f0'}`, background:'#fff' }}>{b.urgency}</span>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:999, color:'#f59e0b', border:'1px solid #fde68a', background:'#fffbeb' }}>{b.status}</span>
                  {b.crossmatch && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999, background:'#fef2f2', color:'#dc2626', border:'1px solid #fca5a5' }}>Crossmatch ✓</span>}
                </div>
              </div>
              {b.indication && <div style={{ fontSize:12.5, color:'#475569', marginTop:6 }}><strong>Indication:</strong> {b.indication}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CHARGE_CATS = ['Room / Bed', 'Consultation', 'Procedure', 'Operation Theatre', 'Anaesthesia', 'Nursing Care', 'Investigation / Lab', 'Radiology', 'Pharmacy / Medicines', 'Consumables', 'ICU Charges', 'Oxygen', 'Blood', 'Ambulance', 'Other'];

function BillingTab({ adm, discharged, onReload, onError, onAdd }) {
  const [form, setForm] = useState({ category: 'Procedure', desc: '', qty: 1, rate: '' });
  const [busy, setBusy] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', remarks: '' });
  const [payBusy, setPayBusy] = useState(false);
  const [activeSection, setActiveSection] = useState('bill'); // 'bill' | 'addcharge' | 'collect'

  const charges = adm.charges || [];
  const chargeSum = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const roomAccrued = Math.max(0, Number(adm.pendingBill || 0) - chargeSum);
  const totalBill = Number(adm.pendingBill || 0);
  const advancePaid = Number(adm.advanceAmount || 0);
  const collections = adm.collections || []; // Additional payments made during stay
  const totalCollected = advancePaid + collections.reduce((s, c) => s + Number(c.amount || 0), 0);
  const balanceDue = Math.max(0, totalBill - totalCollected);
  const excess = Math.max(0, totalCollected - totalBill);

  const daysAdmitted = Math.max(1, Math.floor((Date.now() - new Date(adm.admittedAt || Date.now()).getTime()) / 86400000));

  const addCharge = async () => {
    if (!form.desc.trim() || !(Number(form.rate) > 0)) { onError('Enter a description and rate.'); return; }
    setBusy(true);
    try {
      await ipApi.addAdmissionCharge(adm.admissionId, { ...form, qty: Number(form.qty), rate: Number(form.rate) });
      setForm({ category: 'Procedure', desc: '', qty: 1, rate: '' });
      await onReload();
      setActiveSection('bill');
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  const collectPayment = async () => {
    if (!(Number(payForm.amount) > 0)) { onError('Enter a valid amount.'); return; }
    setPayBusy(true);
    try {
      // Record collection against the admission
      await ipApi.addAdmissionCharge(adm.admissionId, {
        category: 'Payment Received',
        desc: `Payment collected — ${payForm.mode}${payForm.remarks ? ` (${payForm.remarks})` : ''}`,
        qty: 1,
        rate: -Number(payForm.amount), // Negative = credit
      });
      setPayForm({ amount: '', mode: 'Cash', remarks: '' });
      await onReload();
      setActiveSection('bill');
    } catch (e) { onError(e.message); } finally { setPayBusy(false); }
  };

  return (
    <>
      {/* ── Financial Summary Cards ── */}
      <div className="ipd-bill-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <div className="card" style={{ textAlign: 'center', padding: 14, borderTop: '3px solid #6366f1' }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Total Bill</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#0f172a' }}>{inr(totalBill)}</div>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{daysAdmitted} day(s) stay</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 14, borderTop: '3px solid #10b981' }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Advance Paid</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#10b981' }}>{inr(advancePaid)}</div>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{adm.advanceMode || 'Cash'}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 14, borderTop: '3px solid #3b82f6' }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Total Collected</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#3b82f6' }}>{inr(totalCollected)}</div>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>Advance + payments</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${balanceDue > 0 ? '#ef4444' : '#10b981'}` }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{excess > 0 ? 'Excess / Refund' : 'Balance Due'}</div>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: balanceDue > 0 ? '#ef4444' : '#10b981' }}>
            {excess > 0 ? inr(excess) : inr(balanceDue)}
          </div>
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{excess > 0 ? 'Refund to patient' : 'Pending collection'}</div>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      {!discharged && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button type="button" className={`btn ${activeSection === 'addcharge' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveSection(activeSection === 'addcharge' ? 'bill' : 'addcharge')}>
            ➕ Add Service / Charge
          </button>
          <button type="button" className={`btn ${activeSection === 'collect' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveSection(activeSection === 'collect' ? 'bill' : 'collect')}>
            💰 Collect Payment
          </button>
          <button type="button" className={`btn ${activeSection === 'discharge' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveSection(activeSection === 'discharge' ? 'bill' : 'discharge')}>
            ✓ Discharge Checklist
          </button>
        </div>
      )}

      {/* ── Add Charge Form ── */}
      {activeSection === 'addcharge' && !discharged && (
        <div className="card mb" style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 style={{ marginTop: 0 }}>Add Service / Charge to Bill</h3>
          <div className="form-grid mb">
            <div className="field">
              <label>Category</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CHARGE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Description *</label>
              <input className="input" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="e.g. ECG, Dressing, IV Cannula 18G…" />
            </div>
            <div className="field">
              <label>Qty</label>
              <input className="input" type="number" min={1} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
            </div>
            <div className="field">
              <label>Rate ₹ *</label>
              <input className="input" type="number" min={0} value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="Amount per unit" />
            </div>
            {form.rate && form.qty && (
              <div className="field full">
                <div className="info-banner">
                  Line total: <strong>{inr(Number(form.qty) * Number(form.rate))}</strong>
                </div>
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setActiveSection('bill')}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={addCharge}>{busy ? 'Adding…' : 'Add to Bill'}</button>
          </div>
        </div>
      )}

      {/* ── Collect Payment Form ── */}
      {activeSection === 'collect' && !discharged && (
        <div className="card mb" style={{ borderLeft: '4px solid #10b981' }}>
          <h3 style={{ marginTop: 0 }}>💰 Collect Payment from Patient</h3>
          {balanceDue > 0 && (
            <div className="info-banner mb">Balance due: <strong style={{ color: '#ef4444' }}>{inr(balanceDue)}</strong> — collect full or partial amount</div>
          )}
          <div className="form-grid mb">
            <div className="field">
              <label>Amount ₹ *</label>
              <input className="input" type="number" min={1} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder={`Balance: ₹${balanceDue.toLocaleString('en-IN')}`} />
            </div>
            <div className="field">
              <label>Payment Mode</label>
              <select className="input" value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}>
                {['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Insurance'].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Remarks</label>
              <input className="input" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} placeholder="Transaction ID / Cheque no. / Reference" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setActiveSection('bill')}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={payBusy} onClick={collectPayment}>{payBusy ? 'Saving…' : '✓ Record Payment'}</button>
          </div>
        </div>
      )}

      {/* ── Discharge Checklist ── */}
      {activeSection === 'discharge' && !discharged && (
        <DischargeChecklistSection adm={adm} onClose={() => setActiveSection('bill')} onAdd={onAdd} />
      )}

      {/* ── Itemized Bill Table ── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>📄 Itemized Bill</h3>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => window.print()}>🖨 Print Bill</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Date / Time', 'Category', 'Description', 'Qty', 'Rate', 'Amount'].map((h) => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: h === 'Qty' || h === 'Rate' || h === 'Amount' ? 'right' : 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Row 1: Advance paid */}
              {advancePaid > 0 && (
                <tr style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>—</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8', fontSize: 11.5 }}>{adm.admittedAt ? new Date(adm.admittedAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}><span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 999 }}>Advance</span></td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontStyle: 'italic', color: '#16a34a' }}>Advance payment collected at admission ({adm.advanceMode || 'Cash'})</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>1</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#16a34a' }}>-{inr(advancePaid)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>-{inr(advancePaid)}</td>
                </tr>
              )}
              {/* Row 2: Room charges */}
              <tr>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>1</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{dt(adm.admittedAt)}</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>Room / Bed</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>Bed & nursing charges — {adm.ward || 'Ward'} · {adm.bedLabel || adm.bedId}</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{daysAdmitted} day(s)</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{inr(adm.ratePerDay || 0)}/day</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{inr(roomAccrued)}</td>
              </tr>
              {/* Additional charges */}
              {charges.filter((c) => c.category !== 'Payment Received').map((c, i) => (
                <tr key={c.chargeId || i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>{i + 2}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{dt(c.at)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{c.category}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{c.desc}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{c.qty}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{inr(c.rate)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{inr(c.amount)}</td>
                </tr>
              ))}
              {/* Payment rows */}
              {charges.filter((c) => c.category === 'Payment Received').map((c, i) => (
                <tr key={c.chargeId || i} style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>—</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{dt(c.at)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}><span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 999 }}>Payment</span></td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontStyle: 'italic', color: '#16a34a' }}>{c.desc}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>1</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#16a34a' }}>{inr(Math.abs(c.rate))}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>-{inr(Math.abs(c.amount))}</td>
                </tr>
              ))}
              {/* Totals */}
              <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                <td colSpan={6} style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Gross Total</td>
                <td style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontFamily: 'Outfit, sans-serif', fontSize: 15, color: '#0f172a' }}>{inr(totalBill)}</td>
              </tr>
              <tr style={{ background: '#f0fdf4' }}>
                <td colSpan={6} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#16a34a', fontWeight: 700 }}>Total Collected (Advance + Payments)</td>
                <td style={{ padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontFamily: 'Outfit, sans-serif', fontSize: 15, color: '#16a34a' }}>-{inr(totalCollected)}</td>
              </tr>
              <tr style={{ background: balanceDue > 0 ? '#fef2f2' : '#f0fdf4' }}>
                <td colSpan={6} style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 800, color: balanceDue > 0 ? '#dc2626' : '#16a34a', fontSize: 14 }}>
                  {excess > 0 ? '💚 Excess / Refund Due' : '🔴 Balance Due from Patient'}
                </td>
                <td style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontFamily: 'Outfit, sans-serif', fontSize: 18, color: balanceDue > 0 ? '#dc2626' : '#16a34a' }}>
                  {excess > 0 ? inr(excess) : inr(balanceDue)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {!discharged && balanceDue > 0 && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
            ⚠ ₹{balanceDue.toLocaleString('en-IN')} still pending from patient. Collect before discharge.
          </div>
        )}
      </div>
    </>
  );
}

/* ── Procedure Log Tab ── */
const PROCEDURE_TYPES = [
  'IV Cannula Insertion', 'Ryle\'s Tube (RT) Insertion', 'Urinary Catheter Insertion',
  'Wound Dressing', 'Suturing / Wound Closure', 'Chest Drain / Intercostal Drain',
  'Nasogastric (NG) Tube', 'Blood Transfusion', 'ABG Sample Collection',
  'Central Venous Catheter (CVC)', 'ECG', 'Nebulization', 'Oxygen Therapy',
  'Endotracheal Intubation', 'Lumbar Puncture', 'Pleural Tap / Thoracentesis',
  'Ascitic Tap', 'Bone Marrow Aspiration', 'Skin Biopsy', 'Minor OT Procedure', 'Other',
];

function ProcedureLogTab({ adm, discharged, onAdd }) {
  const [form, setForm] = useState({ procedureType: 'IV Cannula Insertion', site: '', performedBy: '', notes: '', complications: 'None' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const procedures = (adm.activity || [])
    .filter((a) => a.kind === 'procedure')
    .map((a) => ({ ...a, ...(a.data || {}) }));

  const save = async () => {
    if (!form.procedureType) return;
    setBusy(true);
    try {
      const title = form.procedureType;
      const note = [
        form.site && `Site: ${form.site}`,
        form.performedBy && `By: ${form.performedBy}`,
        form.notes,
        form.complications && form.complications !== 'None' ? `Complications: ${form.complications}` : '',
      ].filter(Boolean).join(' · ');
      await onAdd({
        kind: 'procedure',
        title,
        note,
        data: { ...form },
      });
      setForm({ procedureType: 'IV Cannula Insertion', site: '', performedBy: '', notes: '', complications: 'None' });
      setShow(false);
    } catch (e) {
      console.error(e);
    } finally { setBusy(false); }
  };

  return (
    <div>
      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShow((s) => !s)}>
            {show ? '✕ Cancel' : '➕ Log New Procedure'}
          </button>
        </div>
      )}
      {show && !discharged && (
        <div className="card mb" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <h3 style={{ marginTop: 0, color: '#8b5cf6' }}>🔬 Log Clinical Procedure</h3>
          <div className="form-grid mb">
            <div className="field">
              <label>Procedure Type *</label>
              <select className="input" value={form.procedureType} onChange={(e) => setForm({ ...form, procedureType: e.target.value })}>
                {PROCEDURE_TYPES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="field"><label>Site / Location</label><input className="input" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="e.g. Right forearm, Left subclavian" /></div>
            <div className="field"><label>Performed By (Doctor/Nurse)</label><input className="input" value={form.performedBy} onChange={(e) => setForm({ ...form, performedBy: e.target.value })} placeholder="Dr. Name / Nurse Name" /></div>
            <div className="field">
              <label>Complications</label>
              <select className="input" value={form.complications} onChange={(e) => setForm({ ...form, complications: e.target.value })}>
                {['None', 'Minor bleeding', 'Hematoma', 'Infection', 'Accidental removal', 'Patient discomfort', 'Other'].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field full"><label>Procedure Notes / Findings</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Details, observations, patient response..." /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShow(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : '✓ Save Procedure Log'}</button>
          </div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>📋 Procedure History</h3>
        {procedures.length === 0
          ? <p className="hint table-empty">No procedures logged yet. Use the button above to log a clinical procedure.</p>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Date / Time', 'Procedure', 'Performed By', 'Complications', 'Notes'].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {procedures.map((p, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>{p.at ? new Date(p.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{p.procedureType || p.title || p.text?.split('—')[0] || '—'}</td>
                    <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{p.performedBy || p.byName || '—'}</td>
                    <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}><span style={{ color: (p.complications || 'None') === 'None' ? '#10b981' : '#ef4444', fontWeight: 600 }}>{p.complications || 'None'}</span></td>
                    <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#64748b' }}>{p.notes || p.note || p.text || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

/* ── Transfer Tab ── */
function TransferTab({ adm, discharged, onReload, onError }) {
  const [form, setForm] = useState({ transferType: 'Ward Transfer', toWard: '', toBed: '', toHospital: '', acceptingDoctor: '', reason: '', notes: '', urgency: 'Routine' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const transfers = (adm.activity || [])
    .filter((a) => a.kind === 'transfer')
    .map((a) => ({ ...a, ...(a.data || {}) }));

  const save = async () => {
    if (!form.reason.trim()) { onError('Please enter a reason for transfer.'); return; }
    setBusy(true);
    try {
      const title = form.transferType;
      const note = form.transferType === 'External Transfer'
        ? `To ${form.toHospital} · ${form.reason} · ${form.urgency}`
        : `To ${form.toWard}${form.toBed ? ` Bed ${form.toBed}` : ''} · ${form.reason} · ${form.urgency}`;
      await ipApi.addAdmissionActivity(adm.admissionId, {
        kind: 'transfer',
        title,
        note,
        data: { ...form },
      });
      // Internal transfer: update admission ward/bed label (bed master occupancy still manual if bedId unknown)
      if (form.transferType !== 'External Transfer' && (form.toWard || form.toBed)) {
        try {
          await ipApi.patchAdmission(adm.admissionId, {
            ward: form.toWard || adm.ward,
            bedLabel: form.toBed
              ? `${form.toWard || adm.ward} · ${form.toBed}`
              : adm.bedLabel,
          });
        } catch (patchErr) {
          onError(patchErr.message || 'Transfer logged but ward/bed update failed');
        }
      }
      setDone(true);
      setForm({ transferType: 'Ward Transfer', toWard: '', toBed: '', toHospital: '', acceptingDoctor: '', reason: '', notes: '', urgency: 'Routine' });
      await onReload();
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      {!discharged && !done && (
        <div className="card mb" style={{ borderLeft: '4px solid #f97316' }}>
          <h3 style={{ marginTop: 0, color: '#f97316' }}>🔄 Patient Transfer</h3>
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
            <strong>Current Location:</strong> {adm.ward} — Bed {adm.bedLabel || adm.bedId} | Dr. {adm.doctor}
          </div>
          <div className="form-grid mb">
            <div className="field">
              <label>Transfer Type</label>
              <select className="input" value={form.transferType} onChange={(e) => setForm({ ...form, transferType: e.target.value })}>
                {['Ward Transfer', 'ICU Transfer', 'Step-down from ICU', 'External Transfer', 'OT Transfer', 'Emergency Transfer'].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Urgency</label>
              <select className="input" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                {['Routine', 'Urgent', 'Emergency'].map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            {form.transferType === 'External Transfer' ? (
              <div className="field full"><label>To Hospital / Facility *</label><input className="input" value={form.toHospital} onChange={(e) => setForm({ ...form, toHospital: e.target.value })} placeholder="Hospital name and city" /></div>
            ) : (
              <>
                <div className="field"><label>To Ward</label><input className="input" value={form.toWard} onChange={(e) => setForm({ ...form, toWard: e.target.value })} placeholder="e.g. ICU, Male Ward, Private Room" /></div>
                <div className="field"><label>To Bed</label><input className="input" value={form.toBed} onChange={(e) => setForm({ ...form, toBed: e.target.value })} placeholder="Bed number / label" /></div>
              </>
            )}
            <div className="field"><label>Accepting Doctor</label><input className="input" value={form.acceptingDoctor} onChange={(e) => setForm({ ...form, acceptingDoctor: e.target.value })} placeholder="Dr. Name who will take over" /></div>
            <div className="field full"><label>Reason for Transfer *</label><input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Deteriorating condition, requires ICU level care, patient request..." /></div>
            <div className="field full"><label>Transfer Notes / Clinical Summary</label><textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Brief clinical summary to send with patient..." /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" style={{ background: '#f97316', borderColor: '#f97316' }} disabled={busy} onClick={save}>{busy ? 'Saving…' : '✓ Record Transfer'}</button>
          </div>
        </div>
      )}
      {done && <div className="info-banner mb">✓ Transfer recorded. Update bed/ward manually in Bed Master.</div>}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>📋 Transfer History</h3>
        {transfers.length === 0
          ? <p className="hint table-empty">No transfers recorded for this admission.</p>
          : transfers.map((t, i) => (
            <div key={i} style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: '#f97316', marginBottom: 4 }}>🔄 {t.transferType || t.title || 'Transfer'} — {t.urgency || 'Routine'}</div>
              <div style={{ color: '#475569' }}>{t.note || t.text || [
                t.toHospital && `To ${t.toHospital}`,
                t.toWard && `To ${t.toWard}${t.toBed ? ` Bed ${t.toBed}` : ''}`,
                t.reason && `Reason: ${t.reason}`,
                t.acceptingDoctor && `Accepting: ${t.acceptingDoctor}`,
              ].filter(Boolean).join(' · ') || '—'}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t.at ? new Date(t.at).toLocaleString('en-IN') : '—'}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ── Consent Forms Tab ── */

const CONSENT_FORMS = [
  {
    id: 'admission',
    label: 'General Admission Consent',
    icon: '📋',
    body: (adm) => `
GENERAL ADMISSION CONSENT

Patient Name: ${adm.patientName || '_______________'}
Admission ID: ${adm.admissionId || '_______________'}
Doctor: ${adm.doctor || '_______________'}
Ward: ${adm.ward || '_______________'} | Bed: ${adm.bedLabel || adm.bedId || '_______________'}
Date: ${new Date().toLocaleDateString('en-IN')}

I, the undersigned patient / guardian, hereby consent to:
1. Admission to the hospital for medical treatment.
2. Routine investigations, laboratory tests, and X-rays as prescribed.
3. Administration of drugs and medications as directed by the treating doctor.
4. Photography / video for medical record or educational purposes (anonymised).
5. Release of information to insurance / TPA as applicable.

I have been informed of my rights as a patient and understand the hospital's
financial policy including the estimated cost of care.

Patient / Guardian Signature: _______________________  Date: ___________
Relationship to patient: _______________________
Witness Signature: _______________________
Staff Signature: _______________________
    `.trim(),
  },
  {
    id: 'surgery',
    label: 'Surgery / Procedure Consent',
    icon: '🔬',
    body: (adm) => `
INFORMED CONSENT FOR SURGICAL / INVASIVE PROCEDURE

Patient Name: ${adm.patientName || '_______________'}
Admission ID: ${adm.admissionId || '_______________'}
Proposed Procedure: _______________________________________________
Surgeon / Doctor: ${adm.doctor || '_______________'}
Date: ${new Date().toLocaleDateString('en-IN')}

I consent to the above procedure and understand:
1. The nature, purpose, and expected benefits of the procedure.
2. Material risks, complications, and alternatives that have been explained to me.
3. Anaesthesia may be required and I consent to its administration.
4. Unforeseen conditions may arise requiring additional procedures.
5. No guarantee of results has been made.

Pre-operative Instructions Understood: ☐ Yes
Allergies / Previous Reactions: _______________________

Patient / Guardian Signature: _______________________  Date: ___________
Surgeon Signature: _______________________
Anaesthetist Signature: _______________________
Witness: _______________________
    `.trim(),
  },
  {
    id: 'lama',
    label: 'LAMA (Leave Against Medical Advice)',
    icon: '⚠️',
    body: (adm) => `
DISCHARGE AGAINST MEDICAL ADVICE (LAMA)

Patient Name: ${adm.patientName || '_______________'}
Admission ID: ${adm.admissionId || '_______________'}
Treating Doctor: ${adm.doctor || '_______________'}
Date: ${new Date().toLocaleDateString('en-IN')}

I, the undersigned, hereby request discharge against the advice of the
treating doctor. I acknowledge:
1. My condition requires continued medical care.
2. I have been clearly informed of the risks of leaving, including deterioration of health and risk to life.
3. The hospital and treating team are NOT responsible for any consequences arising from this decision.
4. I have been offered continued treatment and refuse it of my own free will.
5. I agree to return to the hospital if my condition worsens.

Reason for leaving: _______________________

Patient / Guardian Signature: _______________________  Date: ___________
Relationship: _______________________
Doctor Signature: _______________________
Witness: _______________________
    `.trim(),
  },
];

function ConsentTab({ adm, onAdd, discharged }) {
  const [selected, setSelected] = useState('admission');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const form = CONSENT_FORMS.find((f) => f.id === selected);
  const logged = (adm.activity || [])
    .filter((a) => a.kind === 'consent')
    .map((a) => ({ ...a, ...(a.data || {}) }));

  const handlePrint = async () => {
    const content = form.body(adm);
    const w = window.open('', '_blank', 'width=800,height=700');
    if (!w) return;
    w.document.write(`
      <html><head><title>${escapeHtml(form.label)}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 13px; line-height: 1.7; padding: 40px; color: #111; }
        h2 { font-size: 16px; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 18px; }
        pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; line-height: 1.8; }
      </style></head>
      <body><h2>${escapeHtml(form.label)}</h2><pre>${escapeHtml(content)}</pre></body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
    if (discharged || !onAdd) return;
    setBusy(true);
    setMsg('');
    try {
      await onAdd({
        kind: 'consent',
        title: form.label,
        note: `Consent form printed / issued: ${form.label}`,
        data: { formId: form.id, formLabel: form.label, printedAt: new Date().toISOString() },
      });
      setMsg('Consent issue logged on chart timeline.');
    } catch (e) {
      setMsg(e.message || 'Could not log consent on chart');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="consent-wrap">
      <div className="consent-tabs">
        {CONSENT_FORMS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`consent-tab-btn ${selected === f.id ? 'active' : ''}`}
            onClick={() => setSelected(f.id)}
          >
            <span>{f.icon}</span> {f.label}
          </button>
        ))}
      </div>
      {form && (
        <div className="consent-card card">
          <div className="consent-card-head">
            <span style={{ fontSize: 22 }}>{form.icon}</span>
            <div>
              <h3 style={{ margin: 0 }}>{form.label}</h3>
              <p className="hint" style={{ margin: '4px 0 0' }}>Print for wet signature. Issuing also logs a durable record on the IP chart.</p>
            </div>
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f8fafc', padding: 12, borderRadius: 8, maxHeight: 280, overflow: 'auto' }}>{form.body(adm)}</pre>
          <div className="form-actions" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={handlePrint} disabled={busy}>
              {busy ? 'Logging…' : 'Print & log consent'}
            </button>
            {msg && <span className="hint">{msg}</span>}
          </div>
          {logged.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>Logged consents</h4>
              {logged.map((c) => (
                <div key={c.entryId || c.at} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <strong>{c.formLabel || c.title}</strong> · {c.at ? new Date(c.at).toLocaleString('en-IN') : '—'} · {c.byName || '—'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   eMAR — Electronic Medication Administration Record
   ════════════════════════════════════════════════════════════════════════════ */
const FREQ_SLOTS = {
  'Once Daily (OD)': ['08:00'],
  'Twice Daily (BD)': ['08:00', '20:00'],
  'Thrice Daily (TDS)': ['08:00', '14:00', '20:00'],
  'Four Times Daily (QID)': ['06:00', '12:00', '18:00', '22:00'],
  'Every 6h': ['06:00', '12:00', '18:00', '00:00'],
  'Every 8h': ['06:00', '14:00', '22:00'],
  'Every 12h': ['08:00', '20:00'],
  'Stat (Immediately)': ['STAT'],
  'PRN (As Needed)': ['PRN'],
  'Bedtime (HS)': ['22:00'],
};

const ROUTES = ['Oral (PO)', 'Intravenous (IV)', 'Intramuscular (IM)', 'Subcutaneous (SC)', 'Sublingual (SL)', 'Topical', 'Inhalation', 'Rectal (PR)', 'Nasal', 'Ophthalmic', 'Otic'];
const STATUS_STYLE = {
  Given:    { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  Missed:   { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
  Held:     { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  Due:      { bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
  PRN:      { bg: '#faf5ff', color: '#7c3aed', border: '#c4b5fd' },
  Upcoming: { bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' },
};

function EmarTab({ adm, activity, discharged, onAdd, user }) {
  const loadedOrders = useMemo(() => emarOrdersFromActivity(activity), [activity]);
  const [orders, setOrders] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOrder, setNewOrder] = useState({
    drug: '', dose: '', route: 'Oral (PO)', freq: 'Twice Daily (BD)',
    startDate: new Date().toISOString().slice(0, 10), endDate: '', instructions: '',
  });
  const [markForm, setMarkForm] = useState({ orderId: '', slot: '', status: 'Given', givenBy: '', notes: '' });
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    setOrders(loadedOrders);
  }, [loadedOrders]);

  async function addOrder(e) {
    e.preventDefault();
    if (!newOrder.drug.trim() || !newOrder.dose.trim()) return;
    const slots = FREQ_SLOTS[newOrder.freq] || ['08:00'];
    const today = new Date().toISOString().slice(0, 10);
    const administrations = slots.map((slot) => ({ slot, date: today, status: slot === 'PRN' ? 'PRN' : 'Due' }));
    const order = {
      ...newOrder,
      id: `M${Date.now()}`,
      prescriber: adm.doctor || user?.name || 'Doctor',
      active: true,
      administrations,
    };
    setBusy(true);
    try {
      await onAdd({ kind: 'emar_order', title: order.drug, data: order });
      setNewOrder({ drug: '', dose: '', route: 'Oral (PO)', freq: 'Twice Daily (BD)', startDate: today, endDate: '', instructions: '' });
      setShowAddForm(false);
    } finally {
      setBusy(false);
    }
  }

  function openMarkModal(orderId, slot) {
    setMarkForm({ orderId, slot, status: 'Given', givenBy: user?.name || '', notes: '' });
    setShowMarkModal(true);
  }

  async function saveAdministration() {
    setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await onAdd({
        kind: 'emar_admin',
        title: `${markForm.status} ${markForm.slot}`,
        data: {
          orderId: markForm.orderId,
          slot: markForm.slot,
          date: today,
          status: markForm.status,
          givenBy: markForm.givenBy || user?.name,
          givenAt: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
          notes: markForm.notes,
        },
      });
      setShowMarkModal(false);
    } finally {
      setBusy(false);
    }
  }

  async function stopOrder(id) {
    setBusy(true);
    try {
      await onAdd({ kind: 'emar_stop', title: 'Stop medication', data: { orderId: id } });
    } finally {
      setBusy(false);
    }
  }

  const activeOrders = orders.filter(o => o.active);
  const stoppedOrders = orders.filter(o => !o.active);
  const allAdmin = orders.flatMap(o => o.administrations);
  const givenCount = allAdmin.filter(a => a.status === 'Given').length;
  const missedCount = allAdmin.filter(a => a.status === 'Missed').length;
  const dueCount = allAdmin.filter(a => a.status === 'Due').length;

  return (
    <div>
      {/* Summary bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Active Orders', val: activeOrders.length, color: '#6366f1' },
          { label: 'Given Today',   val: givenCount,          color: '#10b981' },
          { label: 'Due Now',       val: dueCount,            color: '#2563eb' },
          { label: 'Missed',        val: missedCount,         color: '#dc2626' },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 12, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Mark administration modal */}
      {showMarkModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 380, padding: 24 }}>
            <h3 style={{ marginTop: 0, color: '#1a2332' }}>📝 Mark Administration</h3>
            <div className="field mb">
              <label>Status</label>
              <select className="input" value={markForm.status} onChange={e => setMarkForm(f => ({ ...f, status: e.target.value }))}>
                {['Given', 'Missed', 'Held', 'PRN'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="field mb">
              <label>Given By (Nurse/Doctor)</label>
              <input className="input" value={markForm.givenBy} onChange={e => setMarkForm(f => ({ ...f, givenBy: e.target.value }))} placeholder="Staff name" />
            </div>
            <div className="field mb">
              <label>Notes (optional)</label>
              <input className="input" value={markForm.notes} onChange={e => setMarkForm(f => ({ ...f, notes: e.target.value }))} placeholder="Patient refused, dose held, etc." />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowMarkModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={saveAdministration}>✓ Save</button>
            </div>
          </div>
        </div>
      )}

      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowAddForm(s => !s)}>
            {showAddForm ? '✕ Cancel' : '+ Order New Medication'}
          </button>
        </div>
      )}

      {showAddForm && !discharged && (
        <form className="card mb" style={{ borderLeft: '4px solid #6366f1' }} onSubmit={addOrder}>
          <h3 style={{ marginTop: 0, color: '#6366f1' }}>💊 New Medication Order</h3>
          <div className="form-grid mb">
            <div className="field"><label>Drug Name *</label><input className="input" required value={newOrder.drug} onChange={e => setNewOrder(f => ({ ...f, drug: e.target.value }))} placeholder="e.g. Amoxicillin, Paracetamol" /></div>
            <div className="field"><label>Dose *</label><input className="input" required value={newOrder.dose} onChange={e => setNewOrder(f => ({ ...f, dose: e.target.value }))} placeholder="e.g. 500mg, 1g" /></div>
            <div className="field"><label>Route</label><select className="input" value={newOrder.route} onChange={e => setNewOrder(f => ({ ...f, route: e.target.value }))}>{ROUTES.map(r => <option key={r}>{r}</option>)}</select></div>
            <div className="field"><label>Frequency</label><select className="input" value={newOrder.freq} onChange={e => setNewOrder(f => ({ ...f, freq: e.target.value }))}>{Object.keys(FREQ_SLOTS).map(f => <option key={f}>{f}</option>)}</select></div>
            <div className="field"><label>Start Date</label><input type="date" className="input" value={newOrder.startDate} onChange={e => setNewOrder(f => ({ ...f, startDate: e.target.value }))} /></div>
            <div className="field"><label>End Date (blank = ongoing)</label><input type="date" className="input" value={newOrder.endDate} onChange={e => setNewOrder(f => ({ ...f, endDate: e.target.value }))} /></div>
            <div className="field full"><label>Special Instructions</label><input className="input" value={newOrder.instructions} onChange={e => setNewOrder(f => ({ ...f, instructions: e.target.value }))} placeholder="After food, before sleep, with plenty of water..." /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">✓ Add to eMAR</button>
          </div>
        </form>
      )}

      {/* Active medication orders */}
      <div className="card mb">
        <h3 style={{ marginTop: 0 }}>💊 Active Medication Orders — Today's Schedule</h3>
        {activeOrders.length === 0 && <p className="hint table-empty">No active medication orders. Use the button above to add.</p>}
        {activeOrders.map(order => (
          <div key={order.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1a2332' }}>{order.drug} <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>{order.dose}</span></div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{order.route} · {order.freq} · From: {order.startDate}{order.endDate ? ` → ${order.endDate}` : ' (ongoing)'}</div>
                <div style={{ fontSize: 11.5, color: '#94a3b8' }}>Ordered by: Dr. {order.prescriber}{order.instructions ? ` · ${order.instructions}` : ''}</div>
              </div>
              {!discharged && (
                <button type="button" style={{ fontSize: 11, padding: '3px 10px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => stopOrder(order.id)}>✕ Stop Order</button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {order.administrations.length === 0 ? (
                <span style={{ fontSize: 12, color: '#94a3b8', padding: '6px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>PRN — administer as needed</span>
              ) : order.administrations.map((adm2, idx) => {
                const s = STATUS_STYLE[adm2.status] || STATUS_STYLE.Upcoming;
                return (
                  <div key={idx} style={{ padding: '8px 12px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, minWidth: 100, textAlign: 'center' }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: s.color }}>{adm2.slot}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: s.color, marginBottom: 4 }}>{adm2.status}</div>
                    {adm2.givenBy && <div style={{ fontSize: 10, color: '#94a3b8' }}>{adm2.givenBy} {adm2.givenAt}</div>}
                    {!discharged && ['Due', 'Upcoming'].includes(adm2.status) && (
                      <button type="button" onClick={() => openMarkModal(order.id, adm2.slot)} style={{ marginTop: 4, fontSize: 10, padding: '2px 8px', background: s.color, border: 'none', color: '#fff', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>Mark</button>
                    )}
                    {adm2.notes && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontStyle: 'italic' }}>{adm2.notes}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {stoppedOrders.length > 0 && (
        <div className="card" style={{ opacity: 0.6 }}>
          <h4 style={{ marginTop: 0, color: '#94a3b8' }}>⛔ Stopped / Discontinued Orders</h4>
          {stoppedOrders.map(o => (
            <div key={o.id} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f1f5f9', color: '#94a3b8', textDecoration: 'line-through' }}>
              {o.drug} {o.dose} — {o.route} — {o.freq}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Fluid Balance / I&O Chart
   ════════════════════════════════════════════════════════════════════════════ */
function FluidBalanceTab({ adm, activity, discharged, onAdd, user }) {
  const today = new Date().toISOString().slice(0, 10);
  const loadedEntries = useMemo(() => fluidEntriesFromActivity(activity), [activity]);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    type: 'intake', category: 'IV Fluid', detail: '', amount: '',
    time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
    shift: 'Morning', by: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    setEntries(loadedEntries);
  }, [loadedEntries]);

  const INTAKE_CATS = ['IV Fluid', 'Blood / Blood Products', 'Oral', 'NG Feed', 'TPN', 'Medication (IV volume)', 'Other Intake'];
  const OUTPUT_CATS = ['Urine', 'Drain', 'Vomiting', 'NG Aspirate', 'Blood Loss (Intra-op)', 'Stool (Diarrhea)', 'Other Output'];

  const todayEntries = entries.filter(e => e.date === today);
  const totalIntake  = todayEntries.filter(e => e.type === 'intake').reduce((s, e) => s + Number(e.amount), 0);
  const totalOutput  = todayEntries.filter(e => e.type === 'output').reduce((s, e) => s + Number(e.amount), 0);
  const balance      = totalIntake - totalOutput;
  const urineOutput  = todayEntries.filter(e => e.category === 'Urine').reduce((s, e) => s + Number(e.amount), 0);
  const patientWeightKg = adm.weight != null && adm.weight !== '' ? Number(adm.weight) : null;
  const weightForRate = patientWeightKg && patientWeightKg > 0 ? patientWeightKg : null;
  const hoursAdmitted   = Math.max(1, Math.round((Date.now() - new Date(adm.admittedAt || Date.now())) / 3600000));
  const urineRateML     = urineOutput / Math.min(hoursAdmitted, 24);
  const urineRate       = weightForRate ? (urineRateML / weightForRate).toFixed(2) : '—';
  const urineAlert      = !!weightForRate && urineRateML > 0 && Number(urineRate) < 0.5;

  async function addEntry(e) {
    e.preventDefault();
    if (!form.amount || !form.detail.trim()) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        date: today,
        by: form.by || user?.name || '',
      };
      await onAdd({
        kind: 'fluid_balance',
        title: `${form.type} ${form.category}`,
        data: payload,
      });
      setForm((f) => ({ ...f, detail: '', amount: '' }));
      setShowForm(false);
    } finally {
      setBusy(false);
    }
  }

  const shiftGroups = ['Morning', 'Afternoon', 'Night'];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Total Intake (24h)',  val: `${totalIntake} mL`,  color: '#3b82f6' },
          { label: 'Total Output (24h)',  val: `${totalOutput} mL`,  color: '#f97316' },
          { label: 'Fluid Balance',       val: `${balance >= 0 ? '+' : ''}${balance} mL`, color: balance >= 0 ? '#10b981' : '#ef4444' },
          { label: 'Urine Output Rate',  val: weightForRate ? `${urineRate} mL/kg/hr` : 'Set weight on admit', color: urineAlert ? '#ef4444' : '#10b981' },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {urineAlert && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, marginBottom: 14, color: '#dc2626', fontWeight: 700, fontSize: 13 }}>
          ⚠ CRITICAL: Urine output {urineRate} mL/kg/hr — Below 0.5 mL/kg/hr threshold. Notify doctor immediately!
        </div>
      )}

      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Add Fluid Entry'}</button>
        </div>
      )}

      {showForm && !discharged && (
        <form className="card mb" style={{ borderLeft: '4px solid #3b82f6' }} onSubmit={addEntry}>
          <h3 style={{ marginTop: 0, color: '#3b82f6' }}>💧 Add Fluid Entry</h3>
          <div className="form-grid mb">
            <div className="field">
              <label>Type</label>
              <select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value, category: e.target.value === 'intake' ? 'IV Fluid' : 'Urine' }))}>
                <option value="intake">Intake (Input)</option>
                <option value="output">Output</option>
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {(form.type === 'intake' ? INTAKE_CATS : OUTPUT_CATS).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field"><label>Detail / Description *</label><input className="input" required value={form.detail} onChange={e => setForm(f => ({ ...f, detail: e.target.value }))} placeholder="e.g. NS 0.9% 500mL @ 80mL/hr" /></div>
            <div className="field"><label>Amount (mL) *</label><input type="number" min={1} className="input" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
            <div className="field"><label>Time</label><input type="time" className="input" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} /></div>
            <div className="field"><label>Shift</label><select className="input" value={form.shift} onChange={e => setForm(f => ({ ...f, shift: e.target.value }))}><option>Morning</option><option>Afternoon</option><option>Night</option></select></div>
            <div className="field"><label>Recorded By</label><input className="input" value={form.by} onChange={e => setForm(f => ({ ...f, by: e.target.value }))} placeholder="Nurse/Doctor name" /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">✓ Add Entry</button>
          </div>
        </form>
      )}

      {shiftGroups.map(shift => {
        const shiftIn  = todayEntries.filter(e => e.shift === shift && e.type === 'intake');
        const shiftOut = todayEntries.filter(e => e.shift === shift && e.type === 'output');
        if (!shiftIn.length && !shiftOut.length) return null;
        const sIn  = shiftIn.reduce((s, e) => s + Number(e.amount), 0);
        const sOut = shiftOut.reduce((s, e) => s + Number(e.amount), 0);
        return (
          <div key={shift} className="card mb">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h4 style={{ margin: 0 }}>{shift} Shift</h4>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                In: <strong style={{ color: '#3b82f6' }}>{sIn}mL</strong> · Out: <strong style={{ color: '#f97316' }}>{sOut}mL</strong> · Balance: <strong style={{ color: sIn - sOut >= 0 ? '#10b981' : '#ef4444' }}>{sIn - sOut >= 0 ? '+' : ''}{sIn - sOut}mL</strong>
              </span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Time', 'Type', 'Category', 'Detail', 'Amount (mL)', 'By'].map(h => (
                    <th key={h} style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...shiftIn, ...shiftOut].sort((a, b) => a.time.localeCompare(b.time)).map((e, i) => (
                  <tr key={e.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontWeight: 700, color: e.type === 'intake' ? '#3b82f6' : '#f97316' }}>{e.time}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: e.type === 'intake' ? '#eff6ff' : '#fff7ed', color: e.type === 'intake' ? '#2563eb' : '#ea580c', border: `1px solid ${e.type === 'intake' ? '#bfdbfe' : '#fed7aa'}` }}>
                        {e.type === 'intake' ? 'IN' : 'OUT'}
                      </span>
                    </td>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>{e.category}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>{e.detail}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{e.amount}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#94a3b8' }}>{e.by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {todayEntries.length === 0 && <p className="hint table-empty">No fluid entries recorded today. Use the button above to start charting.</p>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Nursing Assessment Tab (Braden, Morse Fall, GCS, Pain)
   ════════════════════════════════════════════════════════════════════════════ */
function NursingAssessmentTab({ adm, discharged, activity, onAdd }) {
  const assessments = (activity || []).filter((e) => e.kind === 'nursing_assessment').map((e) => ({ ...(e.data || e), _entryId: e.entryId }));
  const [form, setForm] = useState({
    assessedBy: '', assessedAt: new Date().toISOString().slice(0, 16), shift: 'Morning',
    braden_sensory: 4, braden_moisture: 4, braden_activity: 4, braden_mobility: 4, braden_nutrition: 3, braden_friction: 2,
    morse_fallHistory: 0, morse_secondaryDx: 0, morse_ambAid: 0, morse_ivAccess: 0, morse_gait: 0, morse_mentalStatus: 0,
    painScore: 0, painLocation: '', painNature: 'Dull',
    gcs_eye: 4, gcs_verbal: 5, gcs_motor: 6,
    generalCondition: 'Fair', skinIntegrity: 'Intact', capillaryRefill: '< 2 sec', nutrition: 'Adequate',
    additionalNotes: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const bradenTotal = form.braden_sensory + form.braden_moisture + form.braden_activity + form.braden_mobility + form.braden_nutrition + form.braden_friction;
  const bradenRisk  = bradenTotal <= 9  ? { label: 'Severe Risk',  color: '#dc2626' }
                    : bradenTotal <= 12 ? { label: 'High Risk',     color: '#ea580c' }
                    : bradenTotal <= 14 ? { label: 'Moderate Risk', color: '#f59e0b' }
                    : bradenTotal <= 18 ? { label: 'Mild Risk',     color: '#84cc16' }
                    :                    { label: 'No Risk',        color: '#10b981' };

  const morseTotal = form.morse_fallHistory + form.morse_secondaryDx + form.morse_ambAid + form.morse_ivAccess + form.morse_gait + form.morse_mentalStatus;
  const morseRisk  = morseTotal >= 45 ? { label: 'High Fall Risk', color: '#dc2626' }
                   : morseTotal >= 25  ? { label: 'Moderate Risk',  color: '#f59e0b' }
                   :                    { label: 'Low Risk',         color: '#10b981' };

  const gcsTotal = form.gcs_eye + form.gcs_verbal + form.gcs_motor;
  const gcsLabel = gcsTotal <= 8  ? { label: 'Severe',       color: '#dc2626' }
                 : gcsTotal <= 12 ? { label: 'Moderate',     color: '#f59e0b' }
                 :                  { label: 'Mild / Normal', color: '#10b981' };

  async function saveAssessment() {
    setBusy(true);
    setErr('');
    try {
      await onAdd({
        kind: 'nursing_assessment',
        title: `Nursing Assessment — ${form.shift} Shift — ${form.assessedBy || 'Nurse'}`,
        note: `Braden: ${bradenTotal}/23 (${bradenRisk.label}), Morse: ${morseTotal} (${morseRisk.label}), GCS: ${gcsTotal}/15, Pain: ${form.painScore}/10`,
        data: { ...form, bradenTotal, bradenRisk: bradenRisk.label, morseTotal, morseRisk: morseRisk.label, gcsTotal },
      });
      setSaved(true);
      setShowForm(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  }

  function scoreRow(label, value, onChange, min, max, options) {
    return (
      <tr key={label}>
        <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontSize: 13, fontWeight: 600 }}>{label}</td>
        <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>
          {options ? (
            <select className="input" style={{ fontSize: 12, padding: '4px 8px' }} value={value} onChange={e => onChange(Number(e.target.value))}>
              {options.map(o => <option key={o.v} value={o.v}>{o.v} — {o.l}</option>)}
            </select>
          ) : (
            <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))} style={{ width: '100%' }} />
          )}
        </td>
        <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontWeight: 800, fontSize: 15, textAlign: 'center', color: '#6366f1' }}>{value}</td>
      </tr>
    );
  }

  return (
    <div>
      {saved && <div className="info-banner mb">✓ Nursing assessment saved successfully!</div>}
      {err && <div className="error mb">{err}</div>}

      {assessments.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Braden Score',     val: `${assessments[0].bradenTotal}/23`, note: assessments[0].bradenRisk, color: assessments[0].bradenTotal <= 14 ? '#ef4444' : '#10b981' },
            { label: 'Morse Fall Score', val: `${assessments[0].morseTotal}`,      note: assessments[0].morseRisk,  color: assessments[0].morseTotal >= 45 ? '#ef4444' : assessments[0].morseTotal >= 25 ? '#f59e0b' : '#10b981' },
            { label: 'GCS Score',        val: `${assessments[0].gcsTotal}/15`,     note: assessments[0].gcsTotal <= 8 ? 'Severe' : assessments[0].gcsTotal <= 12 ? 'Moderate' : 'Normal', color: assessments[0].gcsTotal <= 8 ? '#ef4444' : '#10b981' },
            { label: 'Pain Score',       val: `${assessments[0].painScore}/10`,    note: assessments[0].painScore >= 7 ? 'Severe' : assessments[0].painScore >= 4 ? 'Moderate' : 'Mild/None', color: assessments[0].painScore >= 7 ? '#ef4444' : assessments[0].painScore >= 4 ? '#f59e0b' : '#10b981' },
          ].map(s => (
            <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.note}</div>
            </div>
          ))}
        </div>
      )}

      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ New Assessment'}</button>
        </div>
      )}

      {showForm && (
        <div className="card mb" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <h3 style={{ marginTop: 0, color: '#8b5cf6' }}>🩺 Nursing Assessment</h3>
          <div className="form-grid mb">
            <div className="field"><label>Assessed By</label><input className="input" value={form.assessedBy} onChange={e => setForm(f => ({ ...f, assessedBy: e.target.value }))} placeholder="Nurse name" /></div>
            <div className="field"><label>Date & Time</label><input type="datetime-local" className="input" value={form.assessedAt} onChange={e => setForm(f => ({ ...f, assessedAt: e.target.value }))} /></div>
            <div className="field"><label>Shift</label><select className="input" value={form.shift} onChange={e => setForm(f => ({ ...f, shift: e.target.value }))}><option>Morning</option><option>Afternoon</option><option>Night</option></select></div>
            <div className="field"><label>General Condition</label><select className="input" value={form.generalCondition} onChange={e => setForm(f => ({ ...f, generalCondition: e.target.value }))}>{['Good', 'Fair', 'Guarded', 'Critical', 'Improving', 'Deteriorating'].map(v => <option key={v}>{v}</option>)}</select></div>
          </div>

          <h4 style={{ color: '#6366f1', marginBottom: 8 }}>
            Pressure Ulcer Risk — Braden Scale (total: <span style={{ color: bradenRisk.color }}>{bradenTotal}/23 — {bradenRisk.label}</span>)
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead><tr style={{ background: '#f8fafc' }}><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Parameter</th><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>Score</th><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', width: 60 }}>Value</th></tr></thead>
            <tbody>
              {scoreRow('Sensory Perception (1-4)', form.braden_sensory,  v => setForm(f => ({ ...f, braden_sensory: v })),  1, 4, [{ v: 1, l: 'Completely Limited' }, { v: 2, l: 'Very Limited' }, { v: 3, l: 'Slightly Limited' }, { v: 4, l: 'No Impairment' }])}
              {scoreRow('Moisture (1-4)',            form.braden_moisture,  v => setForm(f => ({ ...f, braden_moisture: v })),  1, 4, [{ v: 1, l: 'Constantly Moist' }, { v: 2, l: 'Very Moist' }, { v: 3, l: 'Occasionally Moist' }, { v: 4, l: 'Rarely Moist' }])}
              {scoreRow('Activity (1-4)',            form.braden_activity,  v => setForm(f => ({ ...f, braden_activity: v })),  1, 4, [{ v: 1, l: 'Bedfast' }, { v: 2, l: 'Chairfast' }, { v: 3, l: 'Walks Occasionally' }, { v: 4, l: 'Walks Frequently' }])}
              {scoreRow('Mobility (1-4)',            form.braden_mobility,  v => setForm(f => ({ ...f, braden_mobility: v })),  1, 4, [{ v: 1, l: 'Completely Immobile' }, { v: 2, l: 'Very Limited' }, { v: 3, l: 'Slightly Limited' }, { v: 4, l: 'No Limitations' }])}
              {scoreRow('Nutrition (1-4)',           form.braden_nutrition, v => setForm(f => ({ ...f, braden_nutrition: v })), 1, 4, [{ v: 1, l: 'Very Poor' }, { v: 2, l: 'Probably Inadequate' }, { v: 3, l: 'Adequate' }, { v: 4, l: 'Excellent' }])}
              {scoreRow('Friction & Shear (1-3)',   form.braden_friction,  v => setForm(f => ({ ...f, braden_friction: v })),  1, 3, [{ v: 1, l: 'Problem' }, { v: 2, l: 'Potential Problem' }, { v: 3, l: 'No Problem' }])}
            </tbody>
          </table>

          <h4 style={{ color: '#0d6e6a', marginBottom: 8 }}>
            Glasgow Coma Scale — GCS (total: <span style={{ color: gcsLabel.color }}>{gcsTotal}/15 — {gcsLabel.label}</span>)
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead><tr style={{ background: '#f8fafc' }}><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Component</th><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>Score</th><th style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', width: 60 }}>Value</th></tr></thead>
            <tbody>
              {scoreRow('Eye Opening (1-4)',     form.gcs_eye,    v => setForm(f => ({ ...f, gcs_eye: v })),    1, 4, [{ v: 1, l: 'None' }, { v: 2, l: 'To Pain' }, { v: 3, l: 'To Voice' }, { v: 4, l: 'Spontaneous' }])}
              {scoreRow('Verbal Response (1-5)', form.gcs_verbal, v => setForm(f => ({ ...f, gcs_verbal: v })), 1, 5, [{ v: 1, l: 'None' }, { v: 2, l: 'Incomprehensible' }, { v: 3, l: 'Inappropriate' }, { v: 4, l: 'Confused' }, { v: 5, l: 'Oriented' }])}
              {scoreRow('Motor Response (1-6)',  form.gcs_motor,  v => setForm(f => ({ ...f, gcs_motor: v })),  1, 6, [{ v: 1, l: 'None' }, { v: 2, l: 'Extension (Decerebrate)' }, { v: 3, l: 'Flexion (Decorticate)' }, { v: 4, l: 'Withdrawal' }, { v: 5, l: 'Localizes Pain' }, { v: 6, l: 'Obeys Commands' }])}
            </tbody>
          </table>

          <h4 style={{ color: '#ef4444', marginBottom: 8 }}>Pain Assessment</h4>
          <div className="form-grid mb">
            <div className="field">
              <label>Pain Score (0 = No pain, 10 = Worst)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="range" min={0} max={10} value={form.painScore} onChange={e => setForm(f => ({ ...f, painScore: Number(e.target.value) }))} style={{ flex: 1 }} />
                <span style={{ fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 900, color: form.painScore >= 7 ? '#dc2626' : form.painScore >= 4 ? '#f59e0b' : '#10b981', minWidth: 32, textAlign: 'center' }}>{form.painScore}</span>
              </div>
            </div>
            <div className="field"><label>Pain Location</label><input className="input" value={form.painLocation} onChange={e => setForm(f => ({ ...f, painLocation: e.target.value }))} placeholder="e.g. Right hypochondrium" /></div>
            <div className="field"><label>Pain Nature</label><select className="input" value={form.painNature} onChange={e => setForm(f => ({ ...f, painNature: e.target.value }))}>{['Dull', 'Sharp', 'Burning', 'Throbbing', 'Stabbing', 'Colicky', 'Radiating', 'Constant', 'Intermittent'].map(v => <option key={v}>{v}</option>)}</select></div>
            <div className="field"><label>Skin Integrity</label><select className="input" value={form.skinIntegrity} onChange={e => setForm(f => ({ ...f, skinIntegrity: e.target.value }))}>{['Intact', 'Grade 1 Ulcer', 'Grade 2 Ulcer', 'Grade 3 Ulcer', 'Grade 4 Ulcer', 'Wound Present', 'Surgical Incision'].map(v => <option key={v}>{v}</option>)}</select></div>
            <div className="field full"><label>Additional Nursing Notes</label><textarea className="input" rows={2} value={form.additionalNotes} onChange={e => setForm(f => ({ ...f, additionalNotes: e.target.value }))} placeholder="Any other clinical observations..." /></div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={saveAssessment}>{busy ? 'Saving…' : '✓ Save Assessment'}</button>
          </div>
        </div>
      )}

      {assessments.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Assessment History</h3>
          {assessments.map(a => (
            <div key={a._entryId || a.id} style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                <div style={{ fontWeight: 700 }}>{new Date(a.assessedAt).toLocaleString('en-IN')} · {a.shift} Shift · {a.assessedBy}</div>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{a.generalCondition}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>Braden: <strong style={{ color: a.bradenTotal <= 14 ? '#ef4444' : '#10b981' }}>{a.bradenTotal}/23</strong> ({a.bradenRisk})</span>
                <span>Morse: <strong style={{ color: a.morseTotal >= 45 ? '#ef4444' : '#10b981' }}>{a.morseTotal}</strong> ({a.morseRisk})</span>
                <span>GCS: <strong>{a.gcsTotal}/15</strong></span>
                <span>Pain: <strong style={{ color: a.painScore >= 7 ? '#ef4444' : a.painScore >= 4 ? '#f59e0b' : '#10b981' }}>{a.painScore}/10</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
      {assessments.length === 0 && !showForm && <p className="hint table-empty">No nursing assessments recorded. Click "+ New Assessment" to begin.</p>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TPA / Insurance Tab
   ════════════════════════════════════════════════════════════════════════════ */
function TpaInsuranceTab({ adm, discharged, onReload, onError }) {
  const [tpa, setTpa] = useState({
    insuranceCompany: adm.insurance || '',
    policyNo: adm.policyNo || '',
    tpaName: adm.tpaName || '',
    memberId: '', groupNo: '', dob: '', abhaId: '',
    preAuthNo: '', preAuthDate: '', preAuthAmount: '',
    preAuthStatus: 'Pending',
    queryDetails: '', queryResponseDate: '',
    finalClaimNo: '', finalClaimDate: '', finalClaimAmount: '',
    approvedAmount: '', patientLiability: '',
    settlementType: 'Cashless',
    settlementStatus: 'Pending',
    tpaRemarks: '',
    admittingDiagnosis: adm.diagnosis || '',
    icdCode: '', roomCategory: 'General Ward',
    ...(adm.tpaDetails || {}),
  });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await ipApi.patchAdmission(adm.admissionId, {
        insurance: tpa.insuranceCompany,
        policyNo: tpa.policyNo,
        tpaName: tpa.tpaName,
        tpaDetails: tpa,
      });
      if (onReload) await onReload();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      if (onError) onError(e.message);
    } finally { setBusy(false); }
  }

  const tpaStatusColors = {
    'Pending': '#f59e0b', 'Approved': '#10b981', 'Partially Approved': '#3b82f6',
    'Rejected': '#ef4444', 'Query': '#f97316', 'Not Applicable': '#94a3b8',
  };

  return (
    <div className="card" style={{ borderLeft: '4px solid #3b82f6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#3b82f6' }}>🛡 TPA / Insurance Management</h3>
        {saved && <span style={{ color: '#10b981', fontWeight: 600, fontSize: 13 }}>✓ Saved</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Pre-Auth Status',   val: tpa.preAuthStatus,  color: tpaStatusColors[tpa.preAuthStatus] || '#94a3b8' },
          { label: 'Settlement Type',   val: tpa.settlementType, color: '#6366f1' },
          { label: 'Approved Amount',   val: tpa.approvedAmount ? `₹${Number(tpa.approvedAmount).toLocaleString('en-IN')}` : 'Pending', color: '#10b981' },
          { label: 'Patient Liability', val: tpa.patientLiability ? `₹${Number(tpa.patientLiability).toLocaleString('en-IN')}` : 'TBD', color: '#ef4444' },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 12, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 16, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="form-grid mb">
        <div className="field"><label>Insurance Company</label><input className="input" value={tpa.insuranceCompany} onChange={e => setTpa(f => ({ ...f, insuranceCompany: e.target.value }))} placeholder="Star Health / HDFC ERGO / etc." disabled={discharged} /></div>
        <div className="field"><label>Policy Number</label><input className="input" value={tpa.policyNo} onChange={e => setTpa(f => ({ ...f, policyNo: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>TPA Name</label><input className="input" value={tpa.tpaName} onChange={e => setTpa(f => ({ ...f, tpaName: e.target.value }))} placeholder="Mediassist, Paramount, etc." disabled={discharged} /></div>
        <div className="field"><label>Member ID / Card No.</label><input className="input" value={tpa.memberId} onChange={e => setTpa(f => ({ ...f, memberId: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Group / Employer No.</label><input className="input" value={tpa.groupNo} onChange={e => setTpa(f => ({ ...f, groupNo: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>ABHA ID (Ayushman Bharat)</label><input className="input" value={tpa.abhaId} onChange={e => setTpa(f => ({ ...f, abhaId: e.target.value }))} placeholder="14-digit ABHA number" disabled={discharged} /></div>
        <div className="field"><label>Admitting Diagnosis</label><input className="input" value={tpa.admittingDiagnosis} onChange={e => setTpa(f => ({ ...f, admittingDiagnosis: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>ICD-10 Code</label><input className="input" value={tpa.icdCode} onChange={e => setTpa(f => ({ ...f, icdCode: e.target.value }))} placeholder="e.g. K35.8" disabled={discharged} /></div>
        <div className="field">
          <label>Room Category</label>
          <select className="input" value={tpa.roomCategory} onChange={e => setTpa(f => ({ ...f, roomCategory: e.target.value }))} disabled={discharged}>
            {['General Ward', 'Semi-Private', 'Private', 'Deluxe', 'Suite', 'ICU', 'NICU', 'HDU'].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <h4 style={{ color: '#6366f1', marginBottom: 10, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>Pre-Authorization</h4>
      <div className="form-grid mb">
        <div className="field"><label>Pre-Auth Request No.</label><input className="input" value={tpa.preAuthNo} onChange={e => setTpa(f => ({ ...f, preAuthNo: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Pre-Auth Request Date</label><input type="date" className="input" value={tpa.preAuthDate} onChange={e => setTpa(f => ({ ...f, preAuthDate: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Pre-Auth Amount Requested ₹</label><input type="number" className="input" value={tpa.preAuthAmount} onChange={e => setTpa(f => ({ ...f, preAuthAmount: e.target.value }))} disabled={discharged} /></div>
        <div className="field">
          <label>Pre-Auth Status</label>
          <select className="input" value={tpa.preAuthStatus} onChange={e => setTpa(f => ({ ...f, preAuthStatus: e.target.value }))} disabled={discharged}>
            {['Pending', 'Approved', 'Partially Approved', 'Rejected', 'Query', 'Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        {tpa.preAuthStatus === 'Query' && (
          <>
            <div className="field full"><label>Query Details from TPA</label><textarea className="input" rows={2} value={tpa.queryDetails} onChange={e => setTpa(f => ({ ...f, queryDetails: e.target.value }))} disabled={discharged} placeholder="Documents requested, clinical query..." /></div>
            <div className="field"><label>Query Response Submitted Date</label><input type="date" className="input" value={tpa.queryResponseDate} onChange={e => setTpa(f => ({ ...f, queryResponseDate: e.target.value }))} disabled={discharged} /></div>
          </>
        )}
      </div>

      <h4 style={{ color: '#10b981', marginBottom: 10, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>Final Claim & Settlement</h4>
      <div className="form-grid mb">
        <div className="field"><label>Final Claim No.</label><input className="input" value={tpa.finalClaimNo} onChange={e => setTpa(f => ({ ...f, finalClaimNo: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Claim Submitted Date</label><input type="date" className="input" value={tpa.finalClaimDate} onChange={e => setTpa(f => ({ ...f, finalClaimDate: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Total Claim Amount ₹</label><input type="number" className="input" value={tpa.finalClaimAmount} onChange={e => setTpa(f => ({ ...f, finalClaimAmount: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Approved Amount ₹</label><input type="number" className="input" value={tpa.approvedAmount} onChange={e => setTpa(f => ({ ...f, approvedAmount: e.target.value }))} disabled={discharged} /></div>
        <div className="field"><label>Patient Liability ₹</label><input type="number" className="input" value={tpa.patientLiability} onChange={e => setTpa(f => ({ ...f, patientLiability: e.target.value }))} disabled={discharged} /></div>
        <div className="field">
          <label>Settlement Type</label>
          <select className="input" value={tpa.settlementType} onChange={e => setTpa(f => ({ ...f, settlementType: e.target.value }))} disabled={discharged}>
            {['Cashless', 'Reimbursement', 'Partial Cashless', 'Not Applicable'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Settlement Status</label>
          <select className="input" value={tpa.settlementStatus} onChange={e => setTpa(f => ({ ...f, settlementStatus: e.target.value }))} disabled={discharged}>
            {['Pending', 'In Process', 'Settled', 'Rejected', 'On Hold'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field full"><label>TPA Remarks / Notes</label><textarea className="input" rows={2} value={tpa.tpaRemarks} onChange={e => setTpa(f => ({ ...f, tpaRemarks: e.target.value }))} disabled={discharged} placeholder="Any communication, documents pending, follow-up dates..." /></div>
      </div>

      {!discharged && (
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : '✓ Save TPA Details'}</button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Discharge Checklist Section (used inside BillingTab)
   ═══════════════════════════════════════════════════════════════════ */
const DISCHARGE_CHECKLIST = [
  { id: 'doc_approval', label: 'Physician discharge order written & signed' },
  { id: 'bills_settled', label: 'Final bill generated & balance collected / insurance settled' },
  { id: 'meds_dispensed', label: 'Discharge medications dispensed from pharmacy' },
  { id: 'instructions_given', label: 'Written discharge instructions given to patient/guardian' },
  { id: 'diet_instructions', label: 'Diet & activity restrictions explained' },
  { id: 'followup_booked', label: 'Follow-up appointment booked (OPD / specialist)' },
  { id: 'reports_given', label: 'Lab / radiology reports given to patient' },
  { id: 'nursing_handover', label: 'Nursing shift handover completed' },
  { id: 'patient_education', label: 'Patient/family education & counseling done' },
  { id: 'transport_arranged', label: 'Transport arranged for patient' },
];

function DischargeChecklistSection({ adm, onClose, onAdd }) {
  const existing = (adm?.activity || []).find((e) => e.kind === 'discharge_planning');
  const savedData = existing?.data || {};
  const [checks, setChecks] = useState(() => Object.fromEntries(
    DISCHARGE_CHECKLIST.map((c) => [c.id, !!savedData.checks?.[c.id]]),
  ));
  const [expectedDate, setExpectedDate] = useState(savedData.expectedDate || '');
  const [conditionAtDischarge, setConditionAtDischarge] = useState(savedData.conditionAtDischarge || 'Improved');
  const [followUpDate, setFollowUpDate] = useState(savedData.followUpDate || '');
  const [followUpDoctor, setFollowUpDoctor] = useState(savedData.followUpDoctor || '');
  const [dischargeType, setDischargeType] = useState(savedData.dischargeType || 'Regular Discharge');
  const [lamaReason, setLamaReason] = useState(savedData.lamaReason || '');
  const [dischargeNote, setDischargeNote] = useState(savedData.dischargeNote || '');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (id) => setChecks((c) => ({ ...c, [id]: !c[id] }));
  const checkedCount = Object.values(checks).filter(Boolean).length;
  const allChecked = checkedCount === DISCHARGE_CHECKLIST.length;

  async function save() {
    if (!onAdd) return;
    setBusy(true);
    setErr('');
    try {
      const payload = {
        checks, expectedDate, conditionAtDischarge, followUpDate, followUpDoctor,
        dischargeType, lamaReason, dischargeNote, allChecked,
      };
      await onAdd({
        kind: 'discharge_planning',
        title: `Discharge planning — ${dischargeType}`,
        note: dischargeNote || `Checklist ${checkedCount}/${DISCHARGE_CHECKLIST.length} complete`,
        data: payload,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (ex) {
      setErr(ex.message || 'Failed to save discharge checklist');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card mb" style={{ borderLeft: '4px solid #10b981' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#0d6e6a' }}>✅ Discharge Planning Checklist</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
      </div>

      {/* Discharge type */}
      <div className="form-grid mb">
        <div className="field">
          <label>Discharge Type</label>
          <select className="input" value={dischargeType} onChange={e => setDischargeType(e.target.value)}>
            {['Regular Discharge', 'LAMA (Left Against Medical Advice)', 'Transfer to Another Hospital', 'Absconded', 'Death'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Condition at Discharge</label>
          <select className="input" value={conditionAtDischarge} onChange={e => setConditionAtDischarge(e.target.value)}>
            {['Improved', 'Cured', 'Stable', 'Same as Admission', 'Deteriorated', 'Critical', 'Deceased'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Expected / Actual Discharge Date</label>
          <input type="date" className="input" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Follow-up Date</label>
          <input type="date" className="input" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Follow-up Doctor / Dept</label>
          <input className="input" value={followUpDoctor} onChange={e => setFollowUpDoctor(e.target.value)} placeholder="e.g. Dr. Sharma — Cardiology" />
        </div>
        {(dischargeType === 'LAMA (Left Against Medical Advice)') && (
          <div className="field full">
            <label style={{ color: '#dc2626', fontWeight: 700 }}>⚠ LAMA Reason (mandatory)</label>
            <textarea className="input" rows={2} value={lamaReason} onChange={e => setLamaReason(e.target.value)} placeholder="Patient/guardian stated reason for leaving AMA. Risks explained and documented." />
          </div>
        )}
        <div className="field full">
          <label>Discharge Summary Note</label>
          <textarea className="input" rows={3} value={dischargeNote} onChange={e => setDischargeNote(e.target.value)} placeholder="Brief summary: course in hospital, final diagnosis, treatment given, discharge medications, follow-up plan..." />
        </div>
      </div>

      {/* Checklist */}
      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Pre-Discharge Verification</div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: allChecked ? '#10b981' : '#f59e0b' }}>{checkedCount} / {DISCHARGE_CHECKLIST.length} completed</div>
        </div>
        {DISCHARGE_CHECKLIST.map(item => (
          <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
            <input type="checkbox" checked={checks[item.id]} onChange={() => toggle(item.id)} style={{ accentColor: '#10b981', width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: checks[item.id] ? '#64748b' : '#1a2332', textDecoration: checks[item.id] ? 'line-through' : 'none', flex: 1 }}>{item.label}</span>
            {checks[item.id] && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✓</span>}
          </label>
        ))}
      </div>

      {!allChecked && (
        <div style={{ padding: '8px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#92400e', marginBottom: 12 }}>
          ⚠ {DISCHARGE_CHECKLIST.length - checkedCount} item(s) pending. Complete all checklist items before discharging.
        </div>
      )}
      {allChecked && (
        <div style={{ padding: '8px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 12.5, color: '#16a34a', fontWeight: 700, marginBottom: 12 }}>
          ✅ All discharge items completed. Patient is ready for discharge.
        </div>
      )}

      {err && <div className="error mb">{err}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy} style={{ background: '#10b981', borderColor: '#10b981' }}>
          {busy ? 'Saving…' : saved ? '✓ Checklist Saved!' : '✅ Save Discharge Checklist'}
        </button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   Doctor Orders Tab
   ═══════════════════════════════════════════════════════════════════════════ */
const ORDER_TYPES = ['General', 'Medication', 'Diet', 'Activity Restriction', 'Nursing', 'Consultation/Referral', 'Procedure', 'Diagnostic/Lab', 'Radiology', 'Physiotherapy', 'Discharge Planning'];
const ORDER_URGENCY = ['Routine', 'Urgent', 'STAT (Immediate)'];

function DoctorOrdersTab({ adm, activity, discharged, onAdd, user }) {
  const loaded = useMemo(() => doctorOrdersFromActivity(activity), [activity]);
  const [orders, setOrders] = React.useState([]);
  const [form, setForm] = React.useState({ type: 'Nursing', text: '', urgency: 'Routine', by: adm.doctor || user?.name || '' });
  const [showForm, setShowForm] = React.useState(false);
  const [ackName, setAckName] = React.useState(user?.name || '');
  const [busy, setBusy] = React.useState(false);
  const [orderMsg, setOrderMsg] = React.useState('');

  React.useEffect(() => {
    setOrders(loaded);
  }, [loaded]);

  async function addOrder(e) {
    e.preventDefault();
    if (!form.text.trim()) return;
    setBusy(true);
    setOrderMsg('');
    try {
      const id = `O${Date.now()}`;
      await onAdd({
        kind: 'doctor_order',
        title: form.type,
        data: {
          id,
          type: form.type,
          text: form.text,
          urgency: form.urgency,
          by: form.by || user?.name,
          at: new Date().toISOString(),
          status: 'Active',
          ackBy: '',
          ackAt: '',
        },
      });
      setForm({ type: 'Nursing', text: '', urgency: 'Routine', by: adm.doctor || user?.name || '' });
      setShowForm(false);
    } catch (err) {
      setOrderMsg(err.message || 'Could not add order');
    } finally {
      setBusy(false);
    }
  }

  async function acknowledge(id) {
    const name = ackName || user?.name || 'Nurse';
    const row = orders.find((o) => o.id === id);
    if (!row) return;
    setBusy(true);
    setOrderMsg('');
    try {
      await onAdd({
        kind: 'doctor_order',
        title: 'Acknowledged',
        refEntryId: id,
        data: { ...row, id, status: 'Acknowledged', ackBy: name, ackAt: new Date().toISOString() },
      });
    } catch (err) {
      setOrderMsg(err.message || 'Could not acknowledge order');
    } finally {
      setBusy(false);
    }
  }

  async function completeOrder(id) {
    const row = orders.find((o) => o.id === id);
    if (!row) return;
    setBusy(true);
    setOrderMsg('');
    try {
      await onAdd({
        kind: 'doctor_order',
        title: 'Completed',
        refEntryId: id,
        data: { ...row, id, status: 'Completed' },
      });
    } catch (err) {
      setOrderMsg(err.message || 'Could not complete order');
    } finally {
      setBusy(false);
    }
  }

  const activeOrders = orders.filter(o => o.status !== 'Completed');
  const completedOrders = orders.filter(o => o.status === 'Completed');
  const urgencyColor = { Routine: '#10b981', Urgent: '#f97316', 'STAT (Immediate)': '#dc2626' };
  const statusColor = { Active: '#f59e0b', Acknowledged: '#3b82f6', Completed: '#94a3b8' };

  return (
    <div>
      {orderMsg && <div className="error mb">{orderMsg}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '8px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Your Name (for acknowledgement):</label>
        <input className="input" style={{ maxWidth: 200 }} value={ackName} onChange={e => setAckName(e.target.value)} placeholder="Nurse / Staff name" />
      </div>
      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Write New Order'}</button>
        </div>
      )}
      {showForm && !discharged && (
        <form className="card mb" style={{ borderLeft: '4px solid #6366f1' }} onSubmit={addOrder}>
          <h3 style={{ marginTop: 0, color: '#6366f1' }}>📋 New Doctor Order</h3>
          <div className="form-grid mb">
            <div className="field"><label>Order Type</label><select className="input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>{ORDER_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
            <div className="field"><label>Urgency</label><select className="input" value={form.urgency} onChange={e => setForm(f => ({ ...f, urgency: e.target.value }))}>{ORDER_URGENCY.map(u => <option key={u}>{u}</option>)}</select></div>
            <div className="field"><label>Ordered By</label><input className="input" value={form.by} onChange={e => setForm(f => ({ ...f, by: e.target.value }))} placeholder="Dr. Name" /></div>
            <div className="field full"><label>Order Details *</label><textarea className="input" rows={3} required value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} placeholder="Write the clinical order clearly and completely..." /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">✓ Write Order</button>
          </div>
        </form>
      )}
      <div className="card mb">
        <h3 style={{ marginTop: 0 }}>📋 Active Orders ({activeOrders.length})</h3>
        {activeOrders.length === 0 && <p className="hint table-empty">No active orders.</p>}
        {activeOrders.map((o, i) => (
          <div key={o.id} style={{ padding: '12px 14px', borderBottom: i < activeOrders.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>{o.type}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fff', color: urgencyColor[o.urgency] || '#64748b', border: `1px solid ${urgencyColor[o.urgency] || '#e2e8f0'}` }}>{o.urgency}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fff', color: statusColor[o.status] || '#64748b', border: `1px solid ${statusColor[o.status] || '#e2e8f0'}` }}>{o.status}</span>
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{new Date(o.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {o.by}</div>
            </div>
            <div style={{ fontSize: 14, color: '#1a2332', lineHeight: 1.5, marginBottom: 8 }}>{o.text}</div>
            {o.status === 'Acknowledged' && <div style={{ fontSize: 11.5, color: '#3b82f6' }}>✓ Acknowledged by {o.ackBy} at {new Date(o.ackAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>}
            {!discharged && (
              <div className="row-actions" style={{ gap: 8, marginTop: 6 }}>
                {o.status === 'Active' && <button type="button" style={{ fontSize: 12, padding: '3px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => acknowledge(o.id)}>✓ Acknowledge</button>}
                <button type="button" style={{ fontSize: 12, padding: '3px 12px', background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => completeOrder(o.id)}>✓ Mark Complete</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {completedOrders.length > 0 && (
        <div className="card" style={{ opacity: 0.65 }}>
          <h4 style={{ marginTop: 0, color: '#94a3b8' }}>✓ Completed Orders ({completedOrders.length})</h4>
          {completedOrders.map(o => (
            <div key={o.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #f1f5f9', color: '#94a3b8', display: 'flex', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>[{o.type}]</span>
              <span style={{ textDecoration: 'line-through', flex: 1 }}>{o.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Shift Handover Tab
   ═══════════════════════════════════════════════════════════════════════════ */
function ShiftHandoverTab({ adm, activity, discharged, onAdd, user }) {
  const loaded = useMemo(() => handoversFromActivity(activity), [activity]);
  const [handovers, setHandovers] = React.useState([]);
  const [form, setForm] = React.useState({
    shift: 'Morning', date: new Date().toISOString().slice(0, 10),
    outgoingNurse: user?.name || '', incomingNurse: '', patientCondition: 'Stable',
    keyEvents: '', pendingTasks: '', alertsGiven: '', medsGiven: '', vitalsLastTaken: '',
  });
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setHandovers(loaded);
  }, [loaded]);

  async function addHandover(e) {
    e.preventDefault();
    if (!form.outgoingNurse.trim() || !form.incomingNurse.trim()) return;
    setBusy(true);
    try {
      await onAdd({
        kind: 'handover',
        title: `${form.shift} handover`,
        data: { ...form, patientName: adm.patientName, admissionId: adm.admissionId },
      });
      setForm({
        shift: 'Morning', date: new Date().toISOString().slice(0, 10),
        outgoingNurse: user?.name || '', incomingNurse: '', patientCondition: 'Stable',
        keyEvents: '', pendingTasks: '', alertsGiven: '', medsGiven: '', vitalsLastTaken: '',
      });
      setShowForm(false);
    } finally {
      setBusy(false);
    }
  }

  const shiftColor = { Morning: '#f59e0b', Afternoon: '#6366f1', Night: '#1e293b' };
  const conditionColor = { Stable: '#10b981', Improving: '#3b82f6', Guarded: '#f97316', Critical: '#dc2626', Deteriorating: '#dc2626' };

  return (
    <div>
      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Add Shift Handover'}</button>
        </div>
      )}
      {showForm && !discharged && (
        <form className="card mb" style={{ borderLeft: '4px solid #6366f1' }} onSubmit={addHandover}>
          <h3 style={{ marginTop: 0, color: '#6366f1' }}>🔄 Shift Handover Record</h3>
          <div className="form-grid mb">
            <div className="field"><label>Shift</label><select className="input" value={form.shift} onChange={e => setForm(f => ({ ...f, shift: e.target.value }))}><option>Morning</option><option>Afternoon</option><option>Night</option></select></div>
            <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div className="field"><label>Outgoing Nurse *</label><input className="input" required value={form.outgoingNurse} onChange={e => setForm(f => ({ ...f, outgoingNurse: e.target.value }))} placeholder="Name of nurse handing over" /></div>
            <div className="field"><label>Incoming Nurse *</label><input className="input" required value={form.incomingNurse} onChange={e => setForm(f => ({ ...f, incomingNurse: e.target.value }))} placeholder="Name of nurse taking over" /></div>
            <div className="field"><label>Patient Condition</label><select className="input" value={form.patientCondition} onChange={e => setForm(f => ({ ...f, patientCondition: e.target.value }))}>{['Stable', 'Improving', 'Guarded', 'Critical', 'Deteriorating'].map(v => <option key={v}>{v}</option>)}</select></div>
            <div className="field"><label>Vitals Last Taken</label><input className="input" value={form.vitalsLastTaken} onChange={e => setForm(f => ({ ...f, vitalsLastTaken: e.target.value }))} placeholder="e.g. 2pm — BP 120/80, P 82, SpO2 98%" /></div>
            <div className="field full"><label>Key Events This Shift</label><textarea className="input" rows={2} value={form.keyEvents} onChange={e => setForm(f => ({ ...f, keyEvents: e.target.value }))} placeholder="Important clinical events, procedures, complications, doctor visits..." /></div>
            <div className="field full"><label>Pending Tasks (for incoming shift)</label><textarea className="input" rows={2} value={form.pendingTasks} onChange={e => setForm(f => ({ ...f, pendingTasks: e.target.value }))} placeholder="Pending dressings, blood draws, medications, orders to follow up..." /></div>
            <div className="field full"><label>Alerts Given to Incoming Nurse</label><textarea className="input" rows={2} value={form.alertsGiven} onChange={e => setForm(f => ({ ...f, alertsGiven: e.target.value }))} placeholder="Watch for deterioration, notify thresholds, isolation precautions reminder..." /></div>
            <div className="field full"><label>Medications Given This Shift</label><input className="input" value={form.medsGiven} onChange={e => setForm(f => ({ ...f, medsGiven: e.target.value }))} placeholder="All meds per eMAR given / any missed or held" /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">✓ Save Handover</button>
          </div>
        </form>
      )}
      {handovers.length === 0 && !showForm && <p className="hint table-empty">No handover records yet.</p>}
      {handovers.map(h => (
        <div key={h.id} className="card mb" style={{ borderLeft: `4px solid ${shiftColor[h.shift] || '#6366f1'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: shiftColor[h.shift] || '#6366f1' }}>{h.shift} Shift Handover · {h.date}</div>
              <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}>
                {h.outgoingNurse} → {h.incomingNurse} ·
                <span style={{ fontWeight: 700, color: conditionColor[h.patientCondition] || '#64748b', marginLeft: 4 }}>Patient: {h.patientCondition}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(h.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          </div>
          {h.vitalsLastTaken && <div style={{ fontSize: 12.5, padding: '6px 10px', background: '#f0fdf4', borderRadius: 6, marginBottom: 8, color: '#16a34a' }}>📊 {h.vitalsLastTaken}</div>}
          {[['Key Events', h.keyEvents], ['Pending Tasks', h.pendingTasks], ['Alerts Given', h.alertsGiven], ['Medications Given', h.medsGiven]].map(([label, value]) => value ? (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{value}</div>
            </div>
          ) : null)}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Care Plan Tab
   ═══════════════════════════════════════════════════════════════════════════ */
const CARE_PLAN_CATEGORIES = ['Medical', 'Nursing', 'Physiotherapy', 'Nutrition', 'Psychosocial', 'Discharge Planning', 'Infection Control', 'Pain Management', 'Patient Education'];

function CarePlanTab({ adm, discharged, activity, onAdd, admissionId, onReload }) {
  const apiPlans = (activity || []).filter((e) => e.kind === 'care_plan').map((e) => ({ ...(e.data || e), _entryId: e.entryId }));
  const [localOverrides, setLocalOverrides] = React.useState({});
  const [savingId, setSavingId] = React.useState('');
  const plans = apiPlans.map((p) => ({ ...p, ...(localOverrides[p._entryId] || {}) }));
  const [form, setForm] = React.useState({ category: 'Medical', problem: '', goal: '', interventions: '', responsible: '', targetDate: '', status: 'Active' });
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  async function addPlan(e) {
    e.preventDefault();
    if (!form.problem.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await onAdd({ kind: 'care_plan', title: form.problem, note: form.goal || form.interventions, data: { ...form } });
      setForm({ category: 'Medical', problem: '', goal: '', interventions: '', responsible: '', targetDate: '', status: 'Active' });
      setShowForm(false);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  }

  async function savePlanUpdate(entryId, patch) {
    if (!admissionId || !entryId) return;
    setSavingId(entryId);
    setErr('');
    try {
      await ipApi.patchAdmissionActivity(admissionId, entryId, { data: patch });
      setLocalOverrides((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
      if (onReload) await onReload();
    } catch (ex) {
      setErr(ex.message || 'Failed to save care plan update');
    } finally {
      setSavingId('');
    }
  }

  const statusColor = { Active: '#3b82f6', Resolved: '#10b981', Ongoing: '#f59e0b', 'On Hold': '#94a3b8' };

  return (
    <div>
      {err && <div className="error mb">{err}</div>}
      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Add Care Plan Entry'}</button>
        </div>
      )}
      {showForm && !discharged && (
        <form className="card mb" style={{ borderLeft: '4px solid #0d6e6a' }} onSubmit={addPlan}>
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>🎯 New Care Plan Entry</h3>
          <div className="form-grid mb">
            <div className="field"><label>Category</label><select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>{CARE_PLAN_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div className="field"><label>Responsible Person / Team</label><input className="input" value={form.responsible} onChange={e => setForm(f => ({ ...f, responsible: e.target.value }))} placeholder="Dr. Name / Nurse / PT" /></div>
            <div className="field"><label>Target Date</label><input type="date" className="input" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))} /></div>
            <div className="field"><label>Status</label><select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{['Active', 'Ongoing', 'Resolved', 'On Hold'].map(s => <option key={s}>{s}</option>)}</select></div>
            <div className="field full"><label>Problem / Nursing Diagnosis *</label><input className="input" required value={form.problem} onChange={e => setForm(f => ({ ...f, problem: e.target.value }))} placeholder="e.g. Risk for infection related to IV access" /></div>
            <div className="field full"><label>Goal / Expected Outcome</label><input className="input" value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} placeholder="e.g. No signs of infection at IV site within 48h" /></div>
            <div className="field full"><label>Interventions / Actions</label><textarea className="input" rows={3} value={form.interventions} onChange={e => setForm(f => ({ ...f, interventions: e.target.value }))} placeholder="Step-by-step nursing/medical interventions..." /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : '✓ Add to Care Plan'}</button>
          </div>
        </form>
      )}
      {plans.length === 0 && !showForm && <p className="hint table-empty">No care plan entries yet.</p>}
      {plans.map(p => (
        <div key={p._entryId || p.id} className="card mb" style={{ borderLeft: `4px solid ${statusColor[p.status] || '#e2e8f0'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{p.problem}</div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>{p.category} · {p.responsible} · Target: {p.targetDate || 'TBD'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: '#f8fafc', color: statusColor[p.status], border: `1px solid ${statusColor[p.status]}` }}>{p.status}</span>
              {!discharged && (
                <select style={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }} value={p.status} onChange={e => {
                  const status = e.target.value;
                  setLocalOverrides(prev => ({ ...prev, [p._entryId]: { ...(prev[p._entryId] || {}), status } }));
                  savePlanUpdate(p._entryId, { status });
                }}>
                  {['Active', 'Ongoing', 'Resolved', 'On Hold'].map(s => <option key={s}>{s}</option>)}
                </select>
              )}
            </div>
          </div>
          {p.goal && <div style={{ fontSize: 12.5, marginBottom: 6 }}><strong>Goal:</strong> {p.goal}</div>}
          {p.interventions && <div style={{ fontSize: 12.5, marginBottom: 8, color: '#475569' }}><strong>Interventions:</strong> {p.interventions}</div>}
          {!discharged && (
            <input className="input" style={{ fontSize: 12 }} value={p.progress || ''} onChange={e => setLocalOverrides(prev => ({ ...prev, [p._entryId]: { ...(prev[p._entryId] || {}), progress: e.target.value } }))} onBlur={e => {
              const progress = e.target.value;
              if (progress !== (apiPlans.find((x) => x._entryId === p._entryId)?.progress || '')) {
                savePlanUpdate(p._entryId, { progress });
              }
            }} placeholder="Progress note (optional)…" />
          )}
          {savingId === p._entryId && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Saving…</div>}
          {p.progress && <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>📝 {p.progress}</div>}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Nutritional Screening Tab (NRS-2002)
   ═══════════════════════════════════════════════════════════════════════════ */
function NutritionalScreeningTab({ adm, discharged, activity, onAdd }) {
  const screenings = (activity || [])
    .filter((e) => e.kind === 'nutrition_screening')
    .map((e) => ({ ...(e.data || e), _entryId: e.entryId, id: e.entryId }));
  const [form, setForm] = React.useState({
    screenedBy: '', screenedAt: new Date().toISOString().slice(0, 16),
    bmiLow: false, weightLoss: false, reducedIntake: false, severelyIll: false,
    nutritionScore: 0, diseaseScore: 0, ageBonus: false,
    dietType: 'Normal', caloricRequirement: '', proteinRequirement: '', fluidRequirement: '',
    dietitianReferral: false, notes: '',
  });
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const nrsTotal = form.nutritionScore + form.diseaseScore + (form.ageBonus ? 1 : 0);
  const nrsRisk = nrsTotal >= 3
    ? { label: 'At Nutritional Risk — Dietitian Referral Required', color: '#dc2626', bg: '#fef2f2' }
    : (form.bmiLow || form.weightLoss || form.reducedIntake || form.severelyIll)
      ? { label: 'Re-screen Weekly', color: '#f59e0b', bg: '#fffbeb' }
      : { label: 'No Nutritional Risk', color: '#10b981', bg: '#f0fdf4' };

  async function saveScreening() {
    if (!onAdd) return;
    setBusy(true);
    setErr('');
    try {
      await onAdd({
        kind: 'nutrition_screening',
        title: `NRS-2002: ${nrsTotal}/7`,
        note: nrsRisk.label,
        data: { ...form, nrsTotal, nrsRisk: nrsRisk.label },
      });
      setShowForm(false);
      setForm({
        screenedBy: '', screenedAt: new Date().toISOString().slice(0, 16),
        bmiLow: false, weightLoss: false, reducedIntake: false, severelyIll: false,
        nutritionScore: 0, diseaseScore: 0, ageBonus: false,
        dietType: 'Normal', caloricRequirement: '', proteinRequirement: '', fluidRequirement: '',
        dietitianReferral: false, notes: '',
      });
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  }

  return (
    <div>
      {err && <div className="error mb">{err}</div>}
      {!discharged && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ New Nutritional Screening'}</button>
        </div>
      )}
      {showForm && !discharged && (
        <div className="card mb" style={{ borderLeft: '4px solid #f97316' }}>
          <h3 style={{ marginTop: 0, color: '#f97316' }}>🥗 Nutritional Screening — NRS 2002</h3>
          <h4 style={{ color: '#475569', fontSize: 12, textTransform: 'uppercase', marginBottom: 8 }}>Part 1 — Initial Screening</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
            {[
              { key: 'bmiLow', label: 'BMI < 20.5 kg/m²' },
              { key: 'weightLoss', label: 'Weight loss in past 3 months' },
              { key: 'reducedIntake', label: 'Reduced dietary intake past week' },
              { key: 'severelyIll', label: 'Severely ill (ICU/ventilated)' },
            ].map(q => (
              <label key={q.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '6px 12px', background: form[q.key] ? '#fff7ed' : '#f8fafc', border: `1px solid ${form[q.key] ? '#fed7aa' : '#e2e8f0'}`, borderRadius: 8, fontWeight: form[q.key] ? 700 : 400 }}>
                <input type="checkbox" checked={form[q.key]} onChange={e => setForm(f => ({ ...f, [q.key]: e.target.checked }))} style={{ accentColor: '#f97316' }} />
                {q.label}
              </label>
            ))}
          </div>
          <h4 style={{ color: '#475569', fontSize: 12, textTransform: 'uppercase', marginBottom: 8 }}>Part 2 — NRS Score</h4>
          <div className="form-grid mb">
            <div className="field">
              <label>Nutritional Impairment Score (0–3)</label>
              <select className="input" value={form.nutritionScore} onChange={e => setForm(f => ({ ...f, nutritionScore: Number(e.target.value) }))}>
                <option value={0}>0 — Normal nutritional status</option>
                <option value={1}>1 — Mild: 5% weight loss in 3 months</option>
                <option value={2}>2 — Moderate: 5% loss in 2 months</option>
                <option value={3}>3 — Severe: 5% loss in 1 month or intake &lt; 25%</option>
              </select>
            </div>
            <div className="field">
              <label>Disease Severity Score (0–3)</label>
              <select className="input" value={form.diseaseScore} onChange={e => setForm(f => ({ ...f, diseaseScore: Number(e.target.value) }))}>
                <option value={0}>0 — No disease / normal requirements</option>
                <option value={1}>1 — Hip fracture, chronic disease, dialysis</option>
                <option value={2}>2 — Major abdominal surgery, stroke</option>
                <option value={3}>3 — Head injury, ICU, bone marrow transplant</option>
              </select>
            </div>
            <div className="field">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.ageBonus} onChange={e => setForm(f => ({ ...f, ageBonus: e.target.checked }))} /> Age ≥ 70 years (+1 point)
              </label>
            </div>
            <div className="field">
              <label style={{ fontWeight: 800 }}>NRS-2002 Total Score</label>
              <div style={{ fontSize: 28, fontWeight: 900, fontFamily: 'Outfit, sans-serif', color: nrsRisk.color, padding: '8px 0' }}>{nrsTotal} / 7</div>
            </div>
          </div>
          <div style={{ padding: '12px 16px', background: nrsRisk.bg, border: `1px solid ${nrsRisk.color}`, borderRadius: 10, marginBottom: 16, fontWeight: 700, color: nrsRisk.color }}>
            {nrsTotal >= 3 ? '⚠ ' : '✓ '}{nrsRisk.label}
          </div>
          <div className="form-grid mb">
            <div className="field"><label>Diet Type</label><select className="input" value={form.dietType} onChange={e => setForm(f => ({ ...f, dietType: e.target.value }))}>{['Normal', 'Diabetic', 'Renal', 'Low Sodium', 'High Protein', 'Soft', 'Liquid', 'NGT Feed', 'TPN', 'NPO'].map(d => <option key={d}>{d}</option>)}</select></div>
            <div className="field"><label>Caloric Requirement (kcal/day)</label><input className="input" value={form.caloricRequirement} onChange={e => setForm(f => ({ ...f, caloricRequirement: e.target.value }))} placeholder="e.g. 1800–2200 kcal" /></div>
            <div className="field"><label>Protein Requirement (g/day)</label><input className="input" value={form.proteinRequirement} onChange={e => setForm(f => ({ ...f, proteinRequirement: e.target.value }))} placeholder="e.g. 65–80 g" /></div>
            <div className="field"><label>Fluid Requirement (mL/day)</label><input className="input" value={form.fluidRequirement} onChange={e => setForm(f => ({ ...f, fluidRequirement: e.target.value }))} placeholder="e.g. 1500–2000 mL" /></div>
            <div className="field"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.dietitianReferral} onChange={e => setForm(f => ({ ...f, dietitianReferral: e.target.checked }))} style={{ accentColor: '#0d6e6a' }} /> Refer to Dietitian</label></div>
            <div className="field full"><label>Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes for dietary team..." /></div>
            <div className="field"><label>Screened By</label><input className="input" value={form.screenedBy} onChange={e => setForm(f => ({ ...f, screenedBy: e.target.value }))} placeholder="Nurse / Dietitian name" /></div>
            <div className="field"><label>Date & Time</label><input type="datetime-local" className="input" value={form.screenedAt} onChange={e => setForm(f => ({ ...f, screenedAt: e.target.value }))} /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={saveScreening} disabled={busy}>{busy ? 'Saving…' : '✓ Save Nutritional Screening'}</button>
          </div>
        </div>
      )}
      {screenings.map(s => (
        <div key={s.id} className="card mb">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>NRS-2002 Score: <span style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.nrsTotal >= 3 ? '#dc2626' : '#10b981' }}>{s.nrsTotal}/7</span></div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>By: {s.screenedBy} · {new Date(s.screenedAt).toLocaleString('en-IN')}</div>
            </div>
            <div style={{ fontSize: 12, padding: '4px 12px', borderRadius: 8, background: s.nrsTotal >= 3 ? '#fef2f2' : '#f0fdf4', color: s.nrsTotal >= 3 ? '#dc2626' : '#10b981', fontWeight: 700 }}>{s.nrsRisk}</div>
          </div>
          <div style={{ fontSize: 12.5, color: '#475569', marginTop: 8 }}>Diet: <strong>{s.dietType}</strong> · Calories: {s.caloricRequirement || 'TBD'} · Protein: {s.proteinRequirement || 'TBD'}{s.dietitianReferral ? ' · ⚕ Dietitian Referral: YES' : ''}</div>
          {s.notes && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>{s.notes}</div>}
        </div>
      ))}
      {screenings.length === 0 && !showForm && <p className="hint table-empty">No nutritional screening done. NABH requires initial screening within 24 hours of admission.</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Incident / Adverse Event Log Tab
   ═══════════════════════════════════════════════════════════════════════════ */
const INCIDENT_TYPES = [
  'Patient Fall', 'Medication Error', 'Wrong Patient/Site/Procedure', 'Pressure Ulcer / Sore',
  'IV Extravasation / Infiltration', 'Healthcare-Associated Infection (HAI)', 'Equipment Failure',
  'Blood Transfusion Reaction', 'Allergic Reaction', 'Near Miss Event', 'Patient Complaint',
  'Surgical / Procedure Complication', 'Code Blue / Rapid Response', 'Patient Elopement', 'Violence / Aggression', 'Other'
];
const SEVERITY_LEVELS = [
  { label: 'Near Miss (no harm)', color: '#94a3b8' },
  { label: 'Minor (no lasting harm)', color: '#10b981' },
  { label: 'Moderate (temporary harm)', color: '#f59e0b' },
  { label: 'Major (permanent harm)', color: '#f97316' },
  { label: 'Sentinel (death / catastrophic)', color: '#dc2626' },
];

function IncidentLogTab({ adm, discharged, activity, onAdd }) {
  const incidents = (activity || []).filter((e) => e.kind === 'incident').map((e) => ({ ...(e.data || e), _entryId: e.entryId, loggedAt: e.at || (e.data || {}).loggedAt }));
  const [form, setForm] = React.useState({
    incidentType: 'Patient Fall', severity: 'Minor (no lasting harm)',
    occurredAt: new Date().toISOString().slice(0, 16),
    location: adm.ward ? `${adm.ward} — Bed ${adm.bedLabel || adm.bedId || ''}` : '',
    description: '', immediateAction: '', reportedTo: '', reportedBy: '',
    followUpRequired: false, followUpDetails: '', investigationStatus: 'Pending',
  });
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  async function saveIncident(e) {
    e.preventDefault();
    if (!form.description.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await onAdd({ kind: 'incident', title: `${form.incidentType} — ${form.severity}`, note: form.description, data: { ...form, loggedAt: new Date().toISOString() } });
      setForm({ incidentType: 'Patient Fall', severity: 'Minor (no lasting harm)', occurredAt: new Date().toISOString().slice(0, 16), location: adm.ward || '', description: '', immediateAction: '', reportedTo: '', reportedBy: '', followUpRequired: false, followUpDetails: '', investigationStatus: 'Pending' });
      setShowForm(false);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  }

  const severityColor = Object.fromEntries(SEVERITY_LEVELS.map(s => [s.label, s.color]));

  return (
    <div>
      {err && <div className="error mb">{err}</div>}
      {incidents.length > 0 && (
        <div style={{ padding: '8px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 14 }}>
          ⚠ {incidents.length} incident{incidents.length > 1 ? 's' : ''} logged for this patient
        </div>
      )}
      <button type="button" className="btn btn-primary" style={{ background: '#dc2626', borderColor: '#dc2626', marginBottom: 14 }} onClick={() => setShowForm(s => !s)}>
        {showForm ? '✕ Cancel' : '⚠ Report New Incident'}
      </button>
      {showForm && (
        <form className="card mb" style={{ borderLeft: '4px solid #dc2626' }} onSubmit={saveIncident}>
          <h3 style={{ marginTop: 0, color: '#dc2626' }}>⚠ Incident / Adverse Event Report</h3>
          <div className="form-grid mb">
            <div className="field"><label>Incident Type *</label><select className="input" value={form.incidentType} onChange={e => setForm(f => ({ ...f, incidentType: e.target.value }))}>{INCIDENT_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
            <div className="field"><label>Severity Level *</label><select className="input" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>{SEVERITY_LEVELS.map(s => <option key={s.label}>{s.label}</option>)}</select></div>
            <div className="field"><label>Date & Time of Incident</label><input type="datetime-local" className="input" value={form.occurredAt} onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value }))} /></div>
            <div className="field"><label>Location</label><input className="input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></div>
            <div className="field"><label>Reported By (Staff Name)</label><input className="input" value={form.reportedBy} onChange={e => setForm(f => ({ ...f, reportedBy: e.target.value }))} placeholder="Nurse / Doctor name" /></div>
            <div className="field"><label>Reported To (Supervisor)</label><input className="input" value={form.reportedTo} onChange={e => setForm(f => ({ ...f, reportedTo: e.target.value }))} placeholder="Nurse in charge / Doctor / QA" /></div>
            <div className="field full"><label>Detailed Description *</label><textarea className="input" rows={3} required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened, when, where, who was involved, what was the patient doing at the time..." /></div>
            <div className="field full"><label>Immediate Action Taken</label><textarea className="input" rows={2} value={form.immediateAction} onChange={e => setForm(f => ({ ...f, immediateAction: e.target.value }))} placeholder="What steps were taken immediately after the incident..." /></div>
            <div className="field"><label>Investigation Status</label><select className="input" value={form.investigationStatus} onChange={e => setForm(f => ({ ...f, investigationStatus: e.target.value }))}>{['Pending', 'In Progress', 'Completed', 'Closed'].map(s => <option key={s}>{s}</option>)}</select></div>
            <div className="field"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.followUpRequired} onChange={e => setForm(f => ({ ...f, followUpRequired: e.target.checked }))} /> Follow-up Action Required</label></div>
            {form.followUpRequired && <div className="field full"><label>Follow-up Details</label><textarea className="input" rows={2} value={form.followUpDetails} onChange={e => setForm(f => ({ ...f, followUpDetails: e.target.value }))} placeholder="Root cause analysis, corrective action, monitoring plan..." /></div>}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ background: '#dc2626', borderColor: '#dc2626' }} disabled={busy}>{busy ? 'Saving…' : '⚠ Submit Incident Report'}</button>
          </div>
        </form>
      )}
      {incidents.length === 0 && !showForm && <p className="hint table-empty">No incidents reported. Use the button above to report any adverse event, near miss, or patient safety concern.</p>}
      {incidents.map(inc => {
        const sColor = severityColor[inc.severity] || '#94a3b8';
        return (
          <div key={inc._entryId || inc.id} className="card mb" style={{ borderLeft: `4px solid ${sColor}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: sColor }}>{inc.incidentType}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Occurred: {new Date(inc.occurredAt).toLocaleString('en-IN')} · {inc.location}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>By: {inc.reportedBy || '—'} → {inc.reportedTo || '—'}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#fff', color: sColor, border: `1px solid ${sColor}` }}>{inc.severity}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>{inc.investigationStatus}</span>
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>{inc.description}</div>
            {inc.immediateAction && <div style={{ fontSize: 12.5, padding: '6px 10px', background: '#f0fdf4', borderRadius: 6, color: '#16a34a' }}>✓ Immediate action: {inc.immediateAction}</div>}
            {inc.followUpRequired && inc.followUpDetails && <div style={{ fontSize: 12.5, padding: '6px 10px', background: '#eff6ff', borderRadius: 6, color: '#2563eb', marginTop: 6 }}>📋 Follow-up: {inc.followUpDetails}</div>}
          </div>
        );
      })}
    </div>
  );
}

